import { type CritHouseRule, type JanusCtx, type Row, activeStatuses, actorId as actorIdOf, ec, ecId, ecLean, handBack, insertRoll, int, inventoryId, isPendingThisDispatch, itemField, record, requiredInt, scalar, upper, definitionNotHeld } from './context.js';
import { inventoryField } from '../../shared/array-types.js';
import { carrierNamesRollType, forcedEntryId } from './statuses/forced-roll-extreme.js';
import { type JanusInput } from './janus.js';
import { rollDiceFaces } from './rolls/roll-dice-arr.js';
import { type SelectedSkill } from './select-skill-for-contesting-roll.js';
import type { CoverTier } from '../actions/cover-defense.js';
import { applyUniversalFortunateOnLuckGain } from './reaction-inventory.js';
import { layerListEntryUuid } from './statuses/status-trigger-gate.js';
import { targetStatusGatesMet } from './statuses/target-status-gate.js';
import { cancelPendingStatus, removeStoredStatus } from './statuses/status-effect-removal-helpers.js';
import { takeRevealedAttackAdv } from './stealth-reveal-carry.js';
import { CT_GATE_KEYS, creatureTypeGateAllows, rollSourceGateAllows } from './creature-type-gate.js';
import { type TriggerGapFn, runTriggerFamily, triggerCall, triggerExecutorGap, triggerFamilyPreDraw } from './trigger-executor.js';
import { type TriggerCandidate, collectTriggerCandidates, gateTriggerCandidate, passesDbGate, predicateMatch, reactionCanFire } from '../reactions/trigger-dispatch.js';
import { janusCtxTriggerReaders } from '../reactions/janus-ctx-trigger-readers.js';
import { ATTACKS_AGAINST_CANNOT_GAIN_ADV, ATTACK_ROLL_TYPE_IDS, clampAttackAdvAgainst, flagSet, rootFlagSet } from './rolls/attacks-against-cannot-gain-adv.js';
import { casterLayerProvider } from '../actions/cast-range-gate.js';
import type { AbilityPreconditionReaders } from '../actions/ability-preconditions.js';
import { isPostRollReactionStatus } from './post-roll-reaction-shape.js';
import { createAbilityConditionEvaluator } from '../compile/ability-conditions.js';
import { answerConditionContext, ConditionContextUnknown } from '../compile/condition-context.js';
import { C1_CONTRACT } from '../compile/possessor-compile.js';
import { findInventoryRow } from '../inventory/flag-adjust.js';
import { readPlayerCharacterField as pcField } from '../display/player-character-field.js';
import { consentAllows } from './trigger-consent.js';
import { casterSkills } from '../pl/passes.js';
import { abilityEntryId } from './charges/charge-entry.js';
import { targetIsAutoCriticalForAttackRoll } from '../port/k3-rules/damage_roll_attack_helpers.js';
import { browserDependencies } from '../port/browser-dependencies.js';
import { applyRawDiceModifiers } from '../raw-dice-modifiers.js';

export const CONTEST_ROLL_ID = 'AD80D31D-898B-66CE-0894-1335D11B1529';
const FORTUNATE_ID = 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8';
// SkillTier::LIST in includes/additions.php; get_skill_tier_from_sp walks it
// from the highest threshold down when ascension awards a point.
const SKILL_TIER_SP = [0, 1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 40, 50,
  60, 80, 100, 120, 140, 160, 180, 200, 220, 240, 260, 280];
const skillTierForSp = (sp: number): number => {
  for (let tier = SKILL_TIER_SP.length - 1; tier >= 0; tier--)
    if (sp >= SKILL_TIER_SP[tier]!) return tier;
  return 0;
};

const ROLL_LAYERS = [
  'skill_roll_mod', 'skill_rolls_adv', 'skill_rolls_dis',
  'roll_type_adv', 'roll_type_dis', 'roll_type_against_adv', 'roll_type_against_dis',
  'roll_type_is_rolled_at_max', 'roll_type_is_rolled_at_min',
  'roll_type_against_is_rolled_at_max', 'roll_type_against_is_rolled_at_min',
  'next_roll_type_adv', 'next_roll_type_dis', 'next_roll_type_against_adv', 'next_roll_type_against_dis',
  'skill_is_rolled_at_max', 'skill_is_rolled_at_min', 'skill_roll_assistance', 'skill_roll_retracting',
  'contesting_roll_adv_vs_spells', 'contesting_roll_dis_vs_spells',
  'ignore_all_attack_roll_dis', 'skill_roll_ignore_dis', 'ascension_skill_point_range_mod',
  'attack_automatically_hits', 'attack_automatically_misses', 'hit_to_crit', 'crit_to_hit',
] as const;
/** The attack-TIER layers: skill_roll_inc.php never reads them — damage_roll.php's attack branch does (hit_to_crit on the used item,
 * crit_to_hit on the target, the auto hit / miss on the used item + its attack context). On a roll with no attack roll type (initiative,
 * a skill check, a save, a contest) a roller's carrier of one is inert: a "Hit to Crit" lingering passive on a PC must not send its
 * Initiative roll to PHP. An attack roll keeps the conservative hand-back. */
const ATTACK_TIER_LAYERS: readonly string[] = ['attack_automatically_hits', 'attack_automatically_misses', 'hit_to_crit', 'crit_to_hit'];
/** Target status gates use the creature this roll actually hits; other held conditions use the shared evaluator. */
const HIT_TO_CRIT_TARGET_GATES: readonly string[] = ['if_target_has_status_effect_active', 'if_target_has_status_effect_not_active'];
/** The layer keys check_ability_conditions() reads (includes/functions/janus/core/check_ability_conditions.php). */
export const isAbilityConditionKey = (key: string): boolean => key.startsWith('if_') || key.startsWith('requires_skill_tier_')
  || key === 'requires_equipped_item' || key === 'ability_active_if_ability_charge_is_at_median' || key === 'draw_sheathed_weapon_for_attack_ability';
/** skill_roll_inc.php ~L965: scalar spell-resistance flags, read by layer_value() on the ROLLER only when PHP's $is_target holds and the used
 * item is a spell carrying skill_roll_contestee — modelled in skillRollArr, so the owner / passive guards skip them (a held status: see
 * skillRollArr's rollerVsSpellsSettled). */
const VS_SPELLS: readonly string[] = ['contesting_roll_adv_vs_spells', 'contesting_roll_dis_vs_spells'];
/** PHP's `!empty(layer_value(...))` on a vs-spells flag. */
const vsSpellsFlagSet = (flag: unknown): boolean =>
  !(flag == null || flag === '' || flag === '0' || flag === 0 || flag === false || (Array.isArray(flag) && !flag.length));
export const ROLL_TRIGGERS =['triggers_on_next_roll_type', 'triggers_on_next_roll_type_against',
  'triggers_when_caster_makes_roll_type', 'target_marked_by_caster'] as const;
/** janus_trigger_carriers() (includes/functions/janus/core/janus_trigger_carriers.php): every bucket janus_trigger() walks. */
const JANUS_TRIGGER_CARRIERS = ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse', 'at_will_spell',
  'innate_spell_per_long_rest', 'spells_known', 'opportunity_attack_ability'] as const;
/** janus_trigger_carrier_entries(): the compiled bucket UNIONED with the root entries (runtime injections), deduped by ability id;
 * spells_known reads its bucket alone. */
function triggerCarrierEntries(ctx: JanusCtx, owner: Row, carrier: string): unknown[] {
  const compiled = record(record(record(owner.computed)?.creature)?.abilities)?.[carrier];
  if (compiled != null && !Array.isArray(compiled)) handBack('skill_roll_arr:trigger-carriers-not-held');
  const base = Array.isArray(compiled) ? compiled : ctx.read.entries(owner, carrier);
  const root = owner[carrier];
  if (carrier === 'spells_known' || !Array.isArray(root)) return base;
  const idOf = (e: unknown): string => abilityEntryId(e).split('|')[0]!;
  const seen = new Set(base.map(idOf).filter(Boolean));
  const out = [...base];
  for (const e of root) { const id = idOf(e); if (id && seen.has(id)) continue; if (id) seen.add(id); out.push(e); }
  return out;
}
/** A layered direct definition or item_arr is held inline; identity shells resolve through the page's definition reader. */
function carrierDefinition(ctx: JanusCtx, entry: unknown): Row {
  const row = record(entry);
  const inline = record(row?.item_arr) ?? row;
  if (inline && record(inline.item_layer_types)) return inline;
  const id = abilityEntryId(entry).split('|')[0]!;
  const definition = ctx.read.definition(id);
  if (!definition) definitionNotHeld('skill_roll_arr:trigger-definition-not-held', id);
  return definition!;
}
// ── skill_roll_arr's PRE-ROLL fire_caster_makes_roll_type_trigger (skill_roll_inc.php ~L423) — IN THE BROWSER (TRAPS 2026-09-28,
// owner: trap checks in combat never go to the server). It dispatches janus_trigger('triggers_when_caster_makes_roll_type') for THIS
// roll's roll_type_1, and every carrier that fires runs through janus() in trigger mode — the RX decision twin
// (reactions/trigger-dispatch.ts) + the shared executor (trigger-executor.ts), exactly as the damage fan-out does. The oracle case is a
// God-Touched PC: its lineage free_ability Favor of the Gods (Skill Roll, 3 charges / long rest) fires on every skill roll — a charge,
// then the ability applied as its own 10-minute status; while that status is HELD it is itself a CMRT carrier (janus_trigger's status
// pool), so the next roll fires twice (tools/probes/traps_check_oracle.php `*_held`). What the executor cannot run hands back PRE-DRAW.
const CMRT_TRIGGER = 'triggers_when_caster_makes_roll_type';
const INCOMING_TRIGGER = 'triggers_on_roll_type_against_the_caster';
const POST_OUTCOME_TRIGGERS = ['triggers_on_hit', 'triggers_on_miss', 'triggers_on_critical_hit', 'triggers_on_not_a_critical_hit',
  'triggers_on_roll_type_against_the_caster_and_hits', 'triggers_on_next_roll_type_against_and_hits',
  'triggers_when_caster_takes_any_damage', 'triggers_when_caster_takes_damage_type'];
const NEXT_ROLL_TRIGGERS = ['triggers_on_next_roll_type', 'triggers_on_next_roll_type_against', 'triggers_on_next_roll_type_against_and_hits'];
/** The on-hit "next roll against the target" rider an ability carries for its target status (Guiding Bolt). */
const AGAINST_RIDER: readonly string[] = ['roll_type_against_adv', 'roll_type_against_dis', 'triggers_on_next_roll_type_against'];
const OWN_RIDER: readonly string[] = ['roll_type_adv', 'roll_type_dis', 'skill_rolls_adv', 'skill_rolls_dis', 'triggers_on_next_roll_type', CMRT_TRIGGER];
/** skill_roll_inc_status_contains_roll_contingency_triggers()'s lifecycle anchors (functions.php): trigger keys that do not gate a roll. */
const LIFECYCLE_TRIGGER_ANCHORS: ReadonlySet<string> = new Set(['triggers_after_short_rest', 'triggers_after_long_rest',
  'triggers_after_combat_begins', 'triggers_when_caster_turn_begins', 'triggers_when_caster_gains_luck']);
/** The roll facts skill_roll_arr's status trigger gates read: the used item's roll_type and PHP's $is_target (undefined = not stated). */
interface NextAgainstRoll { usedRollType: string; rollerIsTarget: boolean | undefined }
/**
 * A held status whose ONLY roll-contingent trigger is triggers_on_next_roll_type_against, carrying nothing else this roll reads but
 * roll_type_against_adv / _dis — the on-hit rider an attack spell leaves on its target (Guiding Bolt: the next attack roll against the
 * glittering target has ADV, effect_ends_on_end_of_caster_next_turn). skill_roll_inc.php, through skill_roll_status_trigger_gates.php:
 *  - TARGET loop (~L775-840; gates_pass + next_roll_should_remove with match_skill_roll_alias false): no used roll_type → the gate fails;
 *    a roller making its OWN target roll ($is_target) passes only when the used roll_type is listed. A passing gate pushes the status's
 *    roll_type_against_adv / _dis once per axis when the roll's effective types (roll_type_1, the used roll_type, the skill_roll alias)
 *    intersect it, then REMOVES the status (remove_status_effect_status_id_single_connection) when the used roll_type is listed.
 *  - CASTER (roller) loops (~L558-690): a non-target roll is inert. A target roll gates and consumes against the used type OR the
 *    skill_roll alias; an unlisted roll is inert and preserves the status.
 * null = not this shape, or the answer depends on an unstated $is_target: the caller keeps its named hand-back.
 */
export function nextRollAgainstRider(read: { has: (row: Row, key: string) => boolean; entries: (row: Row, key: string) => unknown[] },
  definition: Row, side: 'caster' | 'target', rollTypes: ReadonlySet<string>,
  roll: NextAgainstRoll, incomingSettled = false): { delta: number; consume: boolean } | null {
  return nextRollRider(read, definition, side, rollTypes, roll, 'triggers_on_next_roll_type_against', incomingSettled);
}
/** Both one-shot status lanes use the page's layer reader and the same single-connection removal queue. A passive BUCKET entry is the
 * permanent source, not the consumed row; janus_trigger grants the status carrying its next-roll payload. */
export function nextRollRider(read: Parameters<typeof nextRollAgainstRider>[0], definition: Row, side: 'caster' | 'target',
  rollTypes: ReadonlySet<string>, roll: NextAgainstRoll,
  trigger: 'triggers_on_next_roll_type' | 'triggers_on_next_roll_type_against', incomingSettled = false): { delta: number; consume: boolean } | null {
  if (!read.has(definition, trigger)) return null;
  const own = trigger === 'triggers_on_next_roll_type';
  if (own && (side !== 'caster' || !roll.usedRollType)) return null; // no-item checks retain frontEndNextRollCheck's rule
  const allowed: readonly string[] = [...(own ? OWN_RIDER : AGAINST_RIDER),
    ...(incomingSettled ? [INCOMING_TRIGGER, 'target_marked_by_caster', ...ROLL_TYPE_EXTREME_KEYS] : [])];
  const keys = Object.keys(record(definition.item_layer_types) ?? {});
  if (keys.filter((k) => k.includes('trigger') && !LIFECYCLE_TRIGGER_ANCHORS.has(k) && !(own && k === CMRT_TRIGGER)
      && !(incomingSettled && k === INCOMING_TRIGGER)).length !== 1) return null;
  if (keys.some((k) => ([...ROLL_LAYERS, ...ROLL_TRIGGERS] as readonly string[]).includes(k) && !allowed.includes(k))) return null;
  // the caster trigger loop pushes a passing status's skill_roll_adv / _dis (skill_roll_inc.php ~L625-635): not this shape
  if (keys.includes('skill_roll_adv') || keys.includes('skill_roll_dis')) return null;
  if (!incomingSettled && CT_GATE_KEYS.some((k) => keys.includes(k))) return null;
  if (!own && side === 'caster' && roll.rollerIsTarget !== true) return roll.rollerIsTarget === false ? { delta: 0, consume: false } : null;
  const listed = (key: string): Set<string> => new Set(read.entries(definition, key).map((e) => upper(layerListEntryUuid(e))).filter(Boolean));
  const next = listed(trigger);
  const used = upper(roll.usedRollType);
  if (!used) return { delta: 0, consume: false };
  const consumes = next.has(used) || side === 'caster' && next.has(SKILL_ROLL_ALIAS);
  if (!consumes) {
    if (own) return { delta: 0, consume: false };
    if (roll.rollerIsTarget === undefined) return null;
    if (roll.rollerIsTarget) return { delta: 0, consume: false };
  }
  const effective = [...rollTypes, SKILL_ROLL_ALIAS].map(upper);
  let delta = 0;
  for (const [key, sign] of [[own ? 'roll_type_adv' : 'roll_type_against_adv', 1], [own ? 'roll_type_dis' : 'roll_type_against_dis', -1]] as const) {
    if (!read.has(definition, key)) continue;
    const ids = listed(key);
    if (!ids.size) handBack(`skill_roll_arr:unmodelled:${own ? 'caster' : 'target'}.status.${key}-entries`);
    // The roller's compiled source fold counts distinct effective types; the opposing-target loop counts once per axis.
    const hits = [...new Set(effective)].filter(t => ids.has(t)).length;
    if (hits) delta += sign * (side === 'caster' ? hits : 1);
  }
  // Damage-family items make a preliminary contest too; only the damage phase consumes their own next-roll carrier unless it also
  // names the Skill Roll alias (skill_roll_status_next_roll_should_remove's Graviton Cage carve-out).
  return { delta, consume: consumes && (!own || ![DAMAGE_ROLL_TYPE, DOT_ROLL_TYPE].includes(used) || next.has(SKILL_ROLL_ALIAS)) };
}
function cmrtGapFor(incitingHeld: boolean): TriggerGapFn {
  return (ctx, candidate, event) => {
    // The status pool: a held status whose own item is a free_ability (the free ability applied as a lingering status) dispatches
    // through janus() like its carrier does. Anything else from the pool stays unmodelled.
    const type = String(itemField(candidate.ability, 'item_type') ?? '');
    const gap = candidate.pool === 'status'
      ? (type === 'free_ability' ? triggerExecutorGap(ctx, { ...candidate, pool: 'carrier', carrier: 'free_ability' }, event) : `status-pool:item-type:${type}`)
      : triggerExecutorGap(ctx, candidate, event);
    if (gap) return gap;
    // An inciting row the executor does not dispatch with (a trap's host inventory row): PHP's team gate would read it.
    if (!incitingHeld && (ctx.read.has(candidate.ability, 'effect_only_affects_opposing_team_type')
        || ctx.read.has(candidate.ability, 'effect_only_affects_same_team_type'))) return 'team-gate-on-unheld-inciting';
    return null;
  };
}
function fireCasterMakesRollType(ctx: JanusCtx, creature: Row, rollType: string, to: Row | null): void {
  if (ctx.trigger) return; // janus_trigger_in_progress()
  const key = `${rollType}|${ecId(creature)}|${to ? ecId(to) || inventoryId(to) || upper(itemField(to, 'item_id')) : ''}`;
  const fired = ctx.cmrtFired ??= new Set<string>();
  if (fired.has(key)) return;
  const event = { trigger: CMRT_TRIGGER, rollType };
  const inciting = to && ecId(to) && ctx.members.has(ecId(to)) ? to : null;
  const gapOf = cmrtGapFor(inciting !== null);
  triggerFamilyPreDraw(ctx, creature, event, gapOf);
  const before = ctx.rows.length;
  // targets_arr = janus_trigger_resolve_self_targets($caster_arr); a Self-range dispatch lands on the caster either way.
  runTriggerFamily(ctx, event, creature, inciting, [creature], false, gapOf, inciting === null);
  if (ctx.rows.length > before) fired.add(key); // PHP keeps the dedup key only when something dispatched
}
/** Lane A9 (coordinator Codex Round 12 on A8): the roller's triggers_when_caster_makes_roll_type pre-draw checks, READ-ONLY (hand-backs only)
 *  — what fireCasterMakesRollType checks when the roll is made. A contest calls it for every rolling TARGET before the caster's die, so a
 *  target's carrier gap (a Favor of the Gods the page cannot dispatch, a definition not held) hands back before ANY real draw, not only
 *  on the whole-use dry runs. */
export function skillRollCmrtPreflight(ctx: JanusCtx, creature: Row, rollType: string, to: Row | null): void {
  if (ctx.trigger) return;
  const inciting = to && ecId(to) && ctx.members.has(ecId(to)) ? to : null;
  triggerFamilyPreDraw(ctx, creature, { trigger: CMRT_TRIGGER, rollType: upper(rollType) }, cmrtGapFor(inciting !== null));
}
// ── FRONT-END RULE for a check whose used item carries no roll_type (a trap check) — owner 2026-09-28: "all of these things should be
// in front end logic based on JS/TS logic". PHP is NOT the reference here; the browser decides and the trap commit bounds + records it.
// PHP's own behaviour (oracle `*_held`) had two unintended effects this rule removes: a next-roll carrier was never used up by a trap
// check (its used item has no roll_type → the next-roll gate failed), and a held item-as-status re-fired as a status-pool carrier.
//   1. A HELD status whose triggers_on_next_roll_type lists this roll is USED UP by it (its ADV is already counted — the global from
//      held statuses in skillDetails, roll_type_adv in guardRollLayers): statusesRemoved, and it leaves the row the trigger pool reads.
//   2. A CMRT carrier fires only while its own status is NOT active (a held Favor of the Gods IS the buff — no second charge); a held
//      status never re-fires from the status pool.
//   3. The status a fired carrier applies, when it is a next-roll carrier for this roll, lands on THIS roll and is used up by it (not
//      persisted): each Favor of the Gods charge = ADV on one skill roll.
const lists = (ctx: JanusCtx, def: Row, key: string, rollType: string): boolean =>
  ctx.read.entries(def, key).some((e) => upper(layerListEntryUuid(e)) === rollType);
const layerInt = (ctx: JanusCtx, def: Row, key: string): number => {
  const v = ctx.read.value(def, key);
  return v == null || v === '' || v === false ? 0 : requiredInt(v, `skill_roll_arr:invalid-${key}`);
};
function frontEndNextRollCheck(ctx: JanusCtx, creature: Row, rollType: string, to: Row | null, skillId: string, cmrtSuppressed = false): number {
  const id = ecId(creature);
  const held = ctx.read.statuses(creature).filter((s) => !ctx.statusesAdded.includes(s as never));
  const heldItems = new Set(held.map((s) => upper(s.status_item_id)));
  const usedUp = new Set<string>();
  for (const status of held) {
    const definition = ctx.read.definition(upper(status.status_item_id)) ?? record(status.status_arr);
    if (!definition) definitionNotHeld('skill_roll_arr:next-roll-status-definition-not-held', status.status_item_id);
    if (!lists(ctx, definition!, 'triggers_on_next_roll_type', rollType)) continue;
    if (cancelPendingStatus(ctx, creature, status) || !status.status_id || status.tick_added) continue;
    ctx.statusesRemoved.push({ status_id: upper(status.status_id), status_item_id: upper(status.status_item_id), target: id });
    usedUp.add(upper(status.status_id));
  }
  if (usedUp.size) {
    const bucket = record(creature.statuses);
    if (!bucket || !Array.isArray(bucket.target)) handBack('skill_roll_arr:status-list-shape');
    bucket!.target = (bucket!.target as unknown[]).filter((e) => !usedUp.has(upper(record(e)?.status_id)));
  }
  if (ctx.trigger) return 0; // janus_trigger_in_progress(): no CMRT dispatch inside a trigger frame
  if (cmrtSuppressed) return 0; // skill_roll_arr(fire_caster_roll_type_triggers: false) — MV8 stealth contests
  const key = `${rollType}|${id}|${to ? ecId(to) || inventoryId(to) || upper(itemField(to, 'item_id')) : ''}`;
  const fired = ctx.cmrtFired ??= new Set<string>();
  if (fired.has(key)) return 0;
  const inciting = to && ecId(to) && ctx.members.has(ecId(to)) ? to : null;
  const base = { trigger: CMRT_TRIGGER, rollType };
  const { carriers } = triggerCall(() => collectTriggerCandidates(janusCtxTriggerReaders(ctx), creature, base));
  const event = { ...base, restrictToAbilityIds: carriers.map((c) => c.abilityId).filter((a) => !heldItems.has(upper(a))) };
  const gapOf: TriggerGapFn = (gctx, candidate, frameEvent) => candidate.pool === 'status' ? 'status-pool' : cmrtGapFor(inciting !== null)(gctx, candidate, frameEvent);
  triggerFamilyPreDraw(ctx, creature, event, gapOf);
  const addedBefore = ctx.statusesAdded.length, rowsBefore = ctx.rows.length;
  if (event.restrictToAbilityIds.length) runTriggerFamily(ctx, event, creature, inciting, [creature], false, gapOf, inciting === null);
  if (ctx.rows.length > rowsBefore) fired.add(key);
  let delta = 0;
  for (const status of ctx.statusesAdded.slice(addedBefore)) {
    if (upper(status.target) !== id) continue;
    const definition = ctx.read.definition(upper(status.status_item_id));
    if (!definition) definitionNotHeld('skill_roll_arr:cmrt-status-definition-not-held', status.status_item_id);
    if (!lists(ctx, definition!, 'triggers_on_next_roll_type', rollType)) continue;
    for (const k of ROLL_LAYERS) if (!['skill_rolls_adv', 'skill_rolls_dis', 'roll_type_adv', 'roll_type_dis'].includes(k) && ctx.read.has(definition, k))
      handBack(`skill_roll_arr:unmodelled:cmrt-status.${k}`);
    for (const k of ROLL_TRIGGERS) if (k !== 'triggers_on_next_roll_type' && k !== CMRT_TRIGGER && ctx.read.has(definition, k))
      handBack(`skill_roll_arr:unmodelled:cmrt-status.${k}`);
    if (hasSkillScopedModifier(ctx, definition!, skillId)) handBack('skill_roll_arr:unmodelled:cmrt-status.skill-scoped');
    delta += layerInt(ctx, definition!, 'skill_rolls_adv') - layerInt(ctx, definition!, 'skill_rolls_dis')
      + rollTypeDelta(ctx, definition!, rollType, false, 'caster.cmrt-status', undefined, { opposing: to });
    ctx.statusesAdded.splice(ctx.statusesAdded.indexOf(status), 1); // used up by this roll — never persisted
  }
  return delta;
}
/** The pre-roll CMRT dispatch's own next-roll statuses join this inciting roll, then use the held rider's removal path. Skill Roll / used
 *  type membership and ADV axes come from the shared reader above; fresh all-skill globals are absent from the pre-fire skill selection.
 *  Lane A8's Favor of the Gods skill-check payload remains supported (capture …-a8skills AO-3). Other payloads retain their existing guards. */
function consumeCmrtNextRollStatuses(ctx: JanusCtx, creature: Row, addedBefore: number, usedRollType: string, skillId: string,
  rollTypes: ReadonlySet<string>): number {
  if (!usedRollType) return 0;
  const id = ecId(creature);
  let delta = 0;
  for (const status of ctx.statusesAdded.slice(addedBefore)) {
    if (upper(status.target) !== id) continue;
    const definition = ctx.read.definition(upper(status.status_item_id));
    if (!definition) definitionNotHeld('skill_roll_arr:cmrt-status-definition-not-held', status.status_item_id);
    // Fresh globals pass the trigger's membership gate even when damage-family consumption is deferred.
    if (!lists(ctx, definition!, 'triggers_on_next_roll_type', usedRollType) && !lists(ctx, definition!, 'triggers_on_next_roll_type', SKILL_ROLL_ALIAS)) continue;
    const rider = nextRollRider(ctx.read, definition!, 'caster', rollTypes, { usedRollType, rollerIsTarget: false }, 'triggers_on_next_roll_type');
    if (rider) {
      delta += rider.delta + layerInt(ctx, definition!, 'skill_rolls_adv') - layerInt(ctx, definition!, 'skill_rolls_dis');
      if (rider.consume && ctx.statusesAdded.includes(status)) removeStoredStatus(ctx, creature, status as unknown as Row, 'single');
      continue;
    }
    if ([DAMAGE_ROLL_TYPE, DOT_ROLL_TYPE].includes(usedRollType)) continue;
    for (const k of ROLL_LAYERS) if (!['skill_rolls_adv', 'skill_rolls_dis'].includes(k) && ctx.read.has(definition, k))
      handBack(`skill_roll_arr:unmodelled:cmrt-status.${k}`);
    for (const k of ROLL_TRIGGERS) if (k !== 'triggers_on_next_roll_type' && k !== CMRT_TRIGGER && ctx.read.has(definition, k))
      handBack(`skill_roll_arr:unmodelled:cmrt-status.${k}`);
    if (hasSkillScopedModifier(ctx, definition!, skillId)) handBack('skill_roll_arr:unmodelled:cmrt-status.skill-scoped');
    delta += layerInt(ctx, definition!, 'skill_rolls_adv') - layerInt(ctx, definition!, 'skill_rolls_dis');
    ctx.statusesAdded.splice(ctx.statusesAdded.indexOf(status), 1); // read and used up by this roll — never persisted
  }
  return delta;
}
const DAMAGE_ROLL_TYPE = '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0';
const DOT_ROLL_TYPE = '77A044B7-7041-CA96-3C63-BB718B15A364';
/** A status THIS roll's CMRT dispatch just added. skill_roll_arr's status loops read it after the fire (skill_roll_inc.php ~L600): a
 * roll-contingent status passes the trigger loop's gate only when the used item carries a roll_type (a trap's host / Discern Trap
 * carry none, so a next-roll carrier is inert for THIS roll — PHP: 0 ADV, not consumed). Anything else that could touch the roll → PHP. */
function guardCmrtAddedStatuses(ctx: JanusCtx, creature: Row, to: Row | null, addedBefore: number, usedRollType: string, skillId: string,
  rollTypes: ReadonlySet<string>): void {
  for (const status of ctx.statusesAdded.slice(addedBefore)) {
    const target = upper(status.target);
    if (target !== ecId(creature) && !(to && target === ecId(to))) continue;
    const definition = ctx.read.definition(upper(status.status_item_id));
    if (!definition) definitionNotHeld('skill_roll_arr:cmrt-status-definition-not-held', status.status_item_id);
    const touches = [...ROLL_LAYERS, ...ROLL_TRIGGERS, 'skill_roll_adv', 'skill_roll_dis'].some((k) => ctx.read.has(definition, k))
      || hasSkillScopedModifier(ctx, definition!, skillId);
    if (!touches) continue;
    if (target === ecId(creature) && !usedRollType && NEXT_ROLL_TRIGGERS.some((k) => ctx.read.has(definition, k))) continue;
    if (target === ecId(creature) && nextRollRider(ctx.read, definition!, 'caster', rollTypes,
      { usedRollType, rollerIsTarget: false }, 'triggers_on_next_roll_type')) continue; // settled by consumeCmrtNextRollStatuses
    handBack('skill_roll_arr:unmodelled:cmrt-added-status-on-this-roll');
  }
}

/** A definition's own skill-scoped ADV / DIS on this skill: +1 when skill_roll_adv names it, −1 when skill_roll_dis does (build_caster_skills' line). */
function scopedSkillDelta(ctx: JanusCtx, owner: Row, skillId: string | null): number {
  if (!skillId) return 0;
  const names = (key: string): boolean => ctx.read.entries(owner, key).some((entry) => String(itemField(record(entry), 'item_id') ?? entry).toUpperCase() === skillId);
  return (names('skill_roll_adv') ? 1 : 0) - (names('skill_roll_dis') ? 1 : 0);
}
function hasSkillScopedModifier(ctx: JanusCtx, owner: Row, skillId: string | null): boolean {
  if (!skillId) return false;
  return ['skill_roll_adv', 'skill_roll_dis'].some((key) => ctx.read.entries(owner, key).some((entry) =>
    String(itemField(record(entry), 'item_id') ?? entry).toUpperCase() === skillId));
}

/** A held carrier has no compile-time condition verdict. Use the compiler's evaluator with the current roll frame. */
function heldRollConditionsMet(ctx: JanusCtx, carrier: Row, owner: Row,
  reason = 'skill_roll_arr:unmodelled:caster.status.hit_to_crit-conditions'): boolean {
  const current = (row: Row): Row => ({ ...row, statuses: { ...record(row.statuses), target: activeStatuses(ctx, row) } });
  const host = current(owner);
  // The evaluator deliberately ignores unknown if_* families. Keep those explicit at this runtime boundary.
  for (const key of Object.keys(record(carrier.item_layer_types) ?? {})) {
    if (!isAbilityConditionKey(key) || HIT_TO_CRIT_TARGET_GATES.includes(key)) continue;
    if (/^if_(armor_type|weapon_classification|weapon_type|weapon_has_property)_equipped_(not_)?active$/.test(key)
        || /^if_(caster_has_status_effect|light_type|effective_light_type|environment|weather|outdoors|sunlight)_(not_)?active$/.test(key)
        || /^requires_skill_tier_\d+$/.test(key)
        || ['if_caster_summon_within_ft', 'requires_equipped_item', 'ability_active_if_ability_charge_is_at_median', 'draw_sheathed_weapon_for_attack_ability'].includes(key)) continue;
    handBack(`${reason}:${key}`);
  }
  const conditions = createAbilityConditionEvaluator({ catalogRows: C1_CONTRACT.catalogRows, abilityTypes: C1_CONTRACT.abilityTypes,
    aggregationStrategy: name => C1_CONTRACT.catalogRows[name]?.aggregation_strategy ?? 'host_wins',
    resolveCarrierRef: id => { const def = ctx.read.definition(id); if (!def) handBack(`${reason}:definition-not-held:${id}`); return def; },
    resolveInventoryInstance: id => {
      for (const member of ctx.members.values()) { const row = findInventoryRow(member, id); if (row) return row; }
      return null;
    }, queryTemplateDefinition: ctx.read.definition,
    equipmentSlots: C1_CONTRACT.equipmentSlots, vitalityStatusIds: C1_CONTRACT.vitalityStatusIds, skillEntries: C1_CONTRACT.skillEntries,
    conditionSource: (kind, args) => {
      const members = [...ctx.members.values()].map(row => row === owner ? host : current(row));
      const scene = ctx.read.possessorScene?.(host, members);
      const held = scene?.conditionScene?.();
      if (kind === 'present-light' && ctx.read.lightTypesPresent) return ctx.read.lightTypesPresent(host, members);
      try {
        return answerConditionContext(kind, args, { encounter: held?.encounter ?? { ...ctx.encounter, encounter_creatures_arr: members },
          campaign: held?.campaign, value: ctx.read.value, entries: ctx.read.entries, definition: ctx.read.definition,
          abilityTypes: C1_CONTRACT.abilityTypes, equipmentSlots: C1_CONTRACT.equipmentSlots });
      } catch (error) {
        if (error instanceof ConditionContextUnknown) handBack(`${reason}:${error.kind}:${error.why}`);
        throw error;
      }
    } });
  return conditions.check(carrier, host);
}

function hasLuckTrigger(ctx: JanusCtx, creature: Row, key: string): boolean {
  if (ctx.read.has(creature, key)) return true;
  for (const status of activeStatuses(ctx, creature)) {
    const row = record(status);
    const def = ctx.read.definition(String(row?.status_item_id ?? '')) ?? record(row?.status_arr);
    if (!def) definitionNotHeld('skill_roll_arr:status-definition-not-held', String(row?.status_item_id ?? ''));
    if (!ctx.read.has(def, key)) continue;
    // A Fortunate HELD from an earlier gain: modelled by applyUniversalFortunateOnLuckGain (the universal carrier dispatches first and
    // spends the reaction; this status-pool copy then fails the reaction budget — TRAPS oracle, 2026-09-28).
    if (key === 'triggers_when_caster_gains_luck' && upper(row?.status_item_id) === 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8'
        && !isPendingThisDispatch(ctx, status)) continue;
    // A carrier this SAME use just granted (the Fortunate a first natural-20 luck gain applied) — a second gain in one
    // use. Named apart so the pre-draw dry run can recognise it (ability-use.ts, documented residual).
    // …unless the first gain already spent the creature's reaction: Fortunate's own if_caster_has_status_effect_not_active gate
    // fails on the pending copy and the universal carrier needs a reaction (applyUniversalFortunateOnLuckGain returns) — inert, the
    // luck still moves (counterspell oracle AO-158-counter: a self-target Frostbite, two natural 20s).
    if (key === 'triggers_when_caster_gains_luck' && isPendingThisDispatch(ctx, status)
        && upper(row?.status_item_id) === 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8'
        && requiredInt(ecLean(creature, 'reaction') ?? 0, 'caster_gains_luck:reaction-not-held') <= 0) continue;
    if (key === 'triggers_when_caster_gains_luck' && isPendingThisDispatch(ctx, status))
      handBack('caster_gains_luck:second-gain-this-use');
    return true;
  }
  // Every janus_trigger_carriers() bucket (the luck triggers dispatch through janus_trigger too — TRAPS 2026-09-28).
  for (const carrier of JANUS_TRIGGER_CARRIERS) {
    if (carrier === 'reaction_ability' && !ctx.read.reactionInventoryComplete(creature))
      handBack('skill_roll_arr:reaction-inventory-incomplete');
    const entries = triggerCarrierEntries(ctx, creature, carrier);
    if (entries.some((entry) => {
    const row = record(entry);
    if (carrier === 'passive_ability' && row?.passive_conditions_active === false) return false;
    if (carrier === 'reaction_ability' && row?.reaction_conditions_active === false) return false;
    const definition = carrierDefinition(ctx, entry);
    return ctx.read.has(definition, key);
    })) return true;
  }
  return false;
}

function rollTypeDelta(ctx: JanusCtx, source: Row, rollType: string, incoming: boolean, role: string,
  effectiveTypes: ReadonlySet<string> = new Set([rollType]),
  scope?: { opposing: Row | null; targetStatus?: boolean; holder?: Row }): number {
  let delta = 0;
  for (const [key, sign] of [[incoming ? 'roll_type_against_adv' : 'roll_type_adv', 1],
    [incoming ? 'roll_type_against_dis' : 'roll_type_dis', -1]] as const) {
    if (!ctx.read.has(source, key)) continue;
    const entries = ctx.read.entries(source, key);
    if (!entries.length) handBack(`skill_roll_arr:unmodelled:${role}.${key}-entries`);
    if (!entries.some((entry) => effectiveTypes.has(layerListEntryUuid(entry)))) continue;
    if (scope && (!rollSourceGateAllows(ctx, source, scope.opposing, scope.targetStatus === true)
      || scope.holder && !heldRollConditionsMet(ctx, source, scope.holder, `skill_roll_arr:${role}.conditions`))) continue;
    delta += sign;
  }
  return delta;
}

// ── get_caster_roll_type_modifier_sources() over build_caster_roll_type_modifiers()'s source walk (lane A8, 2026-10-02 — the Maimed-on-crit
// save; janus/core/build_caster_roll_type_modifiers.php + get_caster_roll_type_modifier_sources.php). PHP compiles every ALWAYS-ON roll-type
// modifier of the roller into one struct keyed by roll type and reads it per axis for each of the roll's EFFECTIVE roll types (roll_type_1, the
// used item's roll_type, the generic `skill_roll` alias) — a source listing two of them counts twice. Sources (the walk's own order):
//   1. the structured sections (creature_type / lineage / class / subclass / background _arr) — NO catalog item of those types carries a
//      roll_type_* layer (tools/probes/janus_a8_roll_type_sources.php, 2026-10-02), so the page reads none;
//   2. passive_ability (active entries; a follow-AURA passive and a timed combat-begins buff never count from the bucket; a bare-ref passive's
//      holder conditions use the shared current-frame evaluator) and feat (+ one level of nested passive grants);
//   3. reaction_ability rows that CAN FIRE (triggered_reaction_ability_can_fire: charges, and in combat a reaction left) — the adv / dis /
//      against axes only: a held Evade gives its holder the DIS it grants against attack rolls on its OWN save whose used item is an attack;
//   4. equipped inventory + enchantments;
//   5. every live status (trigger-bearing or not, the four axes alike).
// Gates read at lookup: a target-status gate (if_target_has_status_effect_*) on a passive / feat drops its adv / dis when the opposing party is
// unknown (FAIL-CLOSED) and the shared creature-type predicate filters it against a KNOWN one; the
// against axes take no opposing party. skill_roll_arr's is_target walk also drops a STATUS source whose triggers_when_target_gets_item_type_against
// scope does not cover the used item's type or whose caster is not the roll's roll_to.
export const SKILL_ROLL_ALIAS = 'ABF66E1E-DA79-4329-31FA-70D981032C06';
type RollTypeAxis = 'adv' | 'dis' | 'against_adv' | 'against_dis';
const AXIS_LAYER: Record<RollTypeAxis, string> = { adv: 'roll_type_adv', dis: 'roll_type_dis', against_adv: 'roll_type_against_adv', against_dis: 'roll_type_against_dis' };
const AXIS_SIGN: Record<RollTypeAxis, number> = { adv: 1, dis: -1, against_adv: 1, against_dis: -1 };
export function rollTypeModifierStacks(ctx: JanusCtx, roller: Row, rollTypes: readonly string[], axes: readonly RollTypeAxis[],
  scope: { usedItemType: string; usedRollType: string; rollTo: string; opposing: Row | null;
    reactionAllows?: (definition: Row) => boolean; fragmentItemId?: string },
  /** Count only these buckets (the house-rule roll's roll_type_1 adv / dis: guardRollLayers already counts its root / status / passive sources). */
  onlyBuckets: ReadonlySet<string> | null = null, nextAgainstSettled = false): number {
  const types = [...new Set(rollTypes.map(upper).filter((t) => /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(t)))];
  if (!types.length || !axes.length) return 0;
  const reason = (what: string): string => `skill_roll_arr:roll-type-sources:${what}`;
  /** One source's signed stacks for the asked axes (ft_normalize_layer_entries_to_distinct_uuids: an entry listed twice counts once). */
  const stacksOf = (def: Row, bucket: 'passive_ability' | 'feat' | 'reaction_ability' | 'status_effect' | 'inventory' | 'enchantment', status: Row | null): number => {
    let delta = 0;
    if (onlyBuckets && !onlyBuckets.has(bucket)) return 0;
    for (const axis of axes) {
      const key = AXIS_LAYER[axis];
      if (!ctx.read.has(def, key)) continue;
      const listed = new Set(ctx.read.entries(def, key).map(layerListEntryUuid).filter(Boolean));
      if (!listed.size) handBack(reason(`${bucket}.${key}-entries`));
      const hits = types.filter((t) => listed.has(t)).length;
      if (!hits) continue;
      // triggers_if_roll_type_is_at_adv routes the source's ADV to the conditional axis skill_roll_arr never reads
      if (axis === 'adv' && ctx.read.has(def, 'triggers_if_roll_type_is_at_adv')) continue;
      if (axis === 'adv' || axis === 'dis') {
        const statusGated = (bucket === 'passive_ability' || bucket === 'feat')
          && (ctx.read.has(def, 'if_target_has_status_effect_active') || ctx.read.has(def, 'if_target_has_status_effect_not_active'));
        if (!rollSourceGateAllows(ctx, def, scope.opposing, statusGated)) continue;
      } else if (bucket === 'status_effect' && ctx.read.has(def, 'triggers_when_target_gets_item_type_against')) {
        // skill_roll_status_item_type_against_scope_ok + skill_roll_status_against_caster_identity_ok
        const inScope = !!scope.usedItemType && ctx.read.entries(def, 'triggers_when_target_gets_item_type_against').map((e) => String(scalar(e) ?? '')).includes(scope.usedItemType)
          && !!scope.rollTo && upper(status?.status_caster_id ?? status?.caster) === upper(scope.rollTo);
        if (!inScope) continue;
      }
      delta += AXIS_SIGN[axis] * hits;
    }
    return delta;
  };
  const inventory = Array.isArray(roller.inventory) ? roller.inventory : ctx.read.entries(roller, 'inventory');
  const equipped = (row: Row): boolean => (ctx.read.inventoryField?.(row, 'equipped') ?? inventoryField(row, 'equipped')) != null
    || ctx.read.has(row, 'equipped');
  const inventoryStacks = (): number => {
    let delta = 0;
    for (const entry of inventory) {
      const row = record(entry);
      if (!row || !equipped(row)) continue;
      delta += stacksOf(row, 'inventory', null);
      for (const entry of Array.isArray(row.enchantment) ? row.enchantment as unknown[] : []) {
        const source = record(record(entry)?.item_arr) ?? record(entry);
        if (source) delta += stacksOf(source, 'enchantment', null);
      }
    }
    return delta;
  };
  // Ordinary skill rolls already walk their non-gear sources in guardRollLayers.
  if (onlyBuckets && [...onlyBuckets].every(bucket => bucket === 'inventory' || bucket === 'enchantment')) return inventoryStacks();
  const resolve = (entry: unknown, what: string): Row => {
    const row = record(entry);
    const inline = record(row?.item_arr);
    if (inline && record(inline.item_layer_types)) return inline;
    const id = upper(typeof entry === 'string' ? entry.split('|')[0] : inline ? itemField(inline, 'item_id') : row?.item_id ?? row?.ability_id ?? itemField(row, 'item_id'));
    const definition = id ? ctx.read.definition(id) : null;
    if (!definition) handBack(reason(`${what}-definition-not-held`));
    return definition!;
  };
  // 1. the structured sections: the page row carries none and no catalog item of those types holds these layers; a row that DOES carry one is
  //    walked and a contributing entry hands back (Codex A8 r1 P2 — never fail open on an unseen source)
  for (const section of ['creature_type_arr', 'creature_sub_type_arr', 'lineage_arr', 'class_arr', 'subclass_arr', 'background_arr']) {
    const list = roller[section];
    if (list == null) continue;
    if (!Array.isArray(list)) handBack(reason(`section-not-held:${section}`));
    for (const entry of list as unknown[]) {
      const def = record(entry) && record(record(entry)!.item_layer_types) ? record(entry)! : ctx.read.definition(upper(typeof entry === 'string' ? entry : itemField(record(entry), 'item_id')));
      if (!def) handBack(reason(`section-definition-not-held:${section}`));
      if (Object.values(AXIS_LAYER).some((k) => ctx.read.has(def, k))) handBack(reason(`section-source:${section}`));
    }
  }
  const hasIfCondition = (def: Row): boolean => Object.keys(record(def.item_layer_types) ?? {}).some((k) => k.startsWith('if_'));
  let delta = 0;
  // 2. passive_ability + feat (the PC bridge read: computed.creature.abilities first; a creature's root list otherwise)
  const compiled = record(record(record(roller.computed)?.creature)?.abilities);
  const bucketEntries = (bucket: string): unknown[] => {
    const held = compiled?.[bucket];
    if (held != null && !Array.isArray(held)) handBack(reason(`${bucket}-not-held`));
    return Array.isArray(held) ? held : ctx.read.entries(roller, bucket);
  };
  const passiveIds = new Set<string>();
  const passives = bucketEntries('passive_ability');
  // Legacy bound_item_id: a passive granted by equipped gear applies only to that item's own damage fragment,
  // on either axis. Other rolls omit the fragment scope and retain their existing source walk.
  const boundItems = new Map<string, string>();
  if (scope.fragmentItemId !== undefined) for (const entry of inventory) {
    const row = record(entry);
    if (!row) continue;
    const field = (key: string): unknown => ctx.read.inventoryField?.(row, key) ?? inventoryField(row, key);
    if (!equipped(row)) continue;
    const id = upper(field('inventory_id')), itemId = upper(itemField(row, 'item_id') ?? field('inventory_item_id'));
    if (id && itemId) boundItems.set(id, itemId);
  }
  for (const entry of passives) passiveIds.add(upper(typeof entry === 'string' ? entry.split('|')[0] : record(entry)?.item_id ?? record(entry)?.ability_id));
  for (const bucket of ['passive_ability', 'feat'] as const) {
    for (const entry of bucket === 'passive_ability' ? passives : bucketEntries('feat')) {
      const row = record(entry);
      const bare = typeof entry === 'string';
      if (!row && !bare) continue;
      if (bucket === 'passive_ability') {
        const bound = boundItems.get(upper(row?.source));
        if (bound && bound !== upper(scope.fragmentItemId)) continue;
      }
      if (bucket === 'passive_ability' && row && 'passive_conditions_active' in row) {
        const flag = scalar(row.passive_conditions_active);
        if (flag === false || flag == null || flag === 0 || flag === '0' || flag === '') continue;
      }
      const def = resolve(entry, bucket);
      // a status-granted DISPLAY mirror (counted through its status) or a status-NESTED grant (its own if_* conditions) — not modelled
      if (row?.built_from_item_type === 'status_effect') { if (stacksOf(def, bucket, null)) handBack(reason('status-nested-passive')); continue; }
      if (bucket === 'passive_ability' && ((ctx.read.value(def, 'aoe_type') != null && ctx.read.value(def, 'aoe_distance') != null
          && ctx.read.value(def, 'persistent_aoe') != null && ctx.read.value(def, 'aoe_follows_target') != null)
          || (ctx.read.has(def, 'triggers_after_combat_begins') && ctx.read.has(def, 'duration_type')))) continue;
      const own = stacksOf(def, bucket, null);
      if (own && bare && bucket === 'passive_ability' && hasIfCondition(def)
          && !heldRollConditionsMet(ctx, def, roller, reason('bare-passive-conditions'))) continue;
      delta += own;
      if (bucket !== 'feat') continue;
      for (const grant of ctx.read.entries(def, 'passive_ability')) {
        const grantDef = resolve(grant, 'feat-grant');
        const grantId = upper(itemField(grantDef, 'item_id'));
        if (!grantId || passiveIds.has(grantId)) continue;
        passiveIds.add(grantId);
        const n = stacksOf(grantDef, 'feat', null);
        if (n && hasIfCondition(grantDef) && !heldRollConditionsMet(ctx, grantDef, roller, reason('feat-grant-conditions'))) continue;
        delta += n;
      }
    }
  }
  // 3. reaction_ability rows that can fire (array entries only — PHP skips a bare ref here)
  const readers = janusCtxTriggerReaders(ctx);
  for (const entry of bucketEntries('reaction_ability')) {
    const row = record(entry);
    if (!row) continue;
    const def = resolve(entry, 'reaction_ability');
    const n = stacksOf(def, 'reaction_ability', null);
    if (!n) continue;
    if (!passesDbGate(ctx.read, upper(itemField(def, 'item_id')), roller, scope.rollTo || null)) continue;
    if (scope.reactionAllows && !scope.reactionAllows(def)) continue;
    if (!ctx.read.reactionInventoryComplete(roller)) handBack(reason('reaction-inventory-incomplete'));
    if (triggerCall(() => reactionCanFire(readers, row, upper(itemField(def, 'item_id')), roller))) delta += n;
  }
  // 4. equipped gear + enchantments, using the same slot read as boundItems.
  delta += inventoryStacks();
  // 5. live statuses — a row THIS use added counts too (the a8maimed capture: Hand of Radiance's on-hit Exposed gives the target ADV on its own
  //    Maimed save — PHP's signature-keyed rebuild sees it)
  for (const status of activeStatuses(ctx, roller)) {
    const def = ctx.read.definition(upper(status.status_item_id)) ?? record(status.status_arr);
    if (!def) handBack(reason('status-definition-not-held'));
    // guardRollLayers already settled a narrow next-roll against rider (including its inert arm). Do not reapply an unlisted rider
    // through the ungated status-source walk; a consumed rider has already left activeStatuses.
    if (nextAgainstSettled && nextRollAgainstRider(ctx.read, def!, 'caster', new Set(types),
      { usedRollType: scope.usedRollType, rollerIsTarget: true })) continue;
    delta += stacksOf(def!, 'status_effect', status);
  }
  // …and the Evade STATUS a pre-roll Evade put on this roller earlier in the use (ctx.preRollEvadeStatus — PHP's live row the browser never
  // writes). An is_target roll whose used item's roll_type (or the skill_roll alias) is in its triggers_on_next_roll_type_against CONSUMES it
  // (skill_roll_status_next_roll_should_remove in skill_roll_arr's own status loop) — after this roll reads it.
  const rollerId = actorIdOf(roller);
  if (rollerId && ctx.preRollEvadeStatus?.has(rollerId)) {
    const evade = ctx.read.definition(EVADE_ID);
    if (!evade) handBack(reason('evade-definition-not-held'));
    delta += stacksOf(evade!, 'status_effect', { status_caster_id: rollerId });
    if (axes.some((a) => a === 'against_adv' || a === 'against_dis')) {
      const consumes = new Set(ctx.read.entries(evade, 'triggers_on_next_roll_type_against').map(layerListEntryUuid));
      if (consumes.has(upper(scope.usedRollType)) || consumes.has(SKILL_ROLL_ALIAS)) ctx.preRollEvadeStatus.delete(rollerId);
    }
  }
  return delta;
}
const EVADE_ID = 'BE4CCA95-33F0-323D-BA54-844D2540C0F0';

/** Roll-type forces match the used item's type; a compiled skill force has precedence over all of them. */
const ROLL_TYPE_EXTREME_KEYS = new Set(['roll_type_is_rolled_at_max', 'roll_type_is_rolled_at_min',
  'roll_type_against_is_rolled_at_max', 'roll_type_against_is_rolled_at_min']);
const INCOMING_MODIFIER_LAYERS = [...AGAINST_RIDER, 'roll_type_against_is_rolled_at_max', 'roll_type_against_is_rolled_at_min'];
interface RollExtremes { max: boolean; min: boolean }
interface IncomingRollFrame { statuses: Set<string>; passives: Set<string> }
const statusSourceId = (status: Row): string => upper(status.status_id)
  || `${upper(status.status_item_id)}|${upper(status.status_connected_id)}`;
/** A held modifier is already an applied effect. Reading it must not cast its applier again. */
function incomingModifierOnly(definition: Row): boolean {
  return String(itemField(definition, 'item_type') ?? '') === 'status_effect'
    && Object.keys(record(definition.item_layer_types) ?? {}).every(key =>
    [INCOMING_TRIGGER, ...AGAINST_RIDER, ...ROLL_TYPE_EXTREME_KEYS, 'target_marked_by_caster', 'cost_gp', 'unenchantable',
      'duration_int', 'duration_type', 'effect_ends_on_start_of_caster_next_turn', 'effect_ends_on_end_of_caster_next_turn'].includes(key)
    || CT_GATE_KEYS.includes(key) || isAbilityConditionKey(key));
}
/** The roll frame owns its incoming listeners; outcome-dependent reactions remain in the later attack passes. */
function incomingRollFrame(ctx: JanusCtx, owner: Row | null, roller: Row, rollType: string,
  rollerIsTarget: boolean | undefined): IncomingRollFrame {
  const frame: IncomingRollFrame = { statuses: new Set(), passives: new Set() };
  if (!owner) return frame;
  const event = { trigger: INCOMING_TRIGGER, rollType,
    restrictToAbilitiesWithLayer: INCOMING_MODIFIER_LAYERS, excludeAbilitiesWithLayer: POST_OUTCOME_TRIGGERS };
  const previousRead = ctx.read;
  const previousConditions = (previousRead as JanusCtx['read'] & {
    triggerConditionsMet?: (ability: Row, holder: Row) => boolean;
  }).triggerConditionsMet;
  const read: JanusCtx['read'] & { triggerConditionsMet: (ability: Row, holder: Row) => boolean } = {
    ...previousRead, triggerConditionsMet: (ability, holder) =>
      (previousConditions?.(ability, holder) ?? true)
      && (ecId(holder) !== ecId(owner) || !previousRead.has(ability, INCOMING_TRIGGER)
        || creatureTypeGateAllows(ctx, ability, roller) && targetStatusGatesMet(ctx, ability, roller, 'skill_roll_arr:incoming')
          && heldRollConditionsMet(ctx, ability, holder, 'skill_roll_arr:incoming-conditions')),
  };
  ctx.read = read;
  try {
    const readers = janusCtxTriggerReaders(ctx);
    const definitions = [...triggerCarrierEntries(ctx, owner, 'passive_ability').map(entry => carrierDefinition(ctx, entry)),
      ...activeStatuses(ctx, owner).map(status => {
        const definition = ctx.read.definition(upper(status.status_item_id)) ?? record(status.status_arr);
        if (!definition) definitionNotHeld('skill_roll_arr:incoming-status-definition-not-held', status.status_item_id);
        return definition!;
      })];
    if (!definitions.some(definition => ctx.read.has(definition, INCOMING_TRIGGER)
        && !isPostRollReactionStatus(ctx.read, definition) && triggerCall(() => predicateMatch(readers, event, definition)))) return frame;
    // The source walker already resolves identity-only status_arr shells through the page's definition reader.
    // Give the dispatcher the same shallow view; its stored-row precedence must not turn that held definition into an empty listener.
    const triggerOwner: Row = { ...owner, statuses: { ...record(owner.statuses), target: ctx.read.statuses(owner).map(status => ({
      ...status, status_arr: ctx.read.definition(upper(status.status_item_id)) ?? record(status.status_arr),
    })) } };
    const collected = triggerCall(() => collectTriggerCandidates(readers, triggerOwner, event));
    const supports = (candidate: TriggerCandidate): boolean => (candidate.pool === 'status'
      || candidate.carrier === 'passive_ability' && candidate.entry.built_from_item_type !== 'status_effect')
      && !isPostRollReactionStatus(ctx.read, candidate.ability)
      && (candidate.pool !== 'status' || !ctx.read.has(candidate.ability, 'target_marked_by_caster')
        || upper(ctx.read.value(candidate.entry, 'status_caster_id') ?? candidate.entry.caster) === ecId(roller));
    const candidates = [...collected.carriers, ...collected.statuses].filter(supports);
    // Modifier rows can have a foreign applier: carrier authority governs casting, not an already-held effect's payload.
    const heldModifiers = activeStatuses(ctx, owner).flatMap(status => {
      const definition = ctx.read.definition(upper(status.status_item_id)) ?? record(status.status_arr);
      if (!definition) definitionNotHeld('skill_roll_arr:incoming-status-definition-not-held', status.status_item_id);
      if (!ctx.read.has(definition, INCOMING_TRIGGER) || !incomingModifierOnly(definition!)
          || !triggerCall(() => predicateMatch(readers, event, definition!))) return [];
      return [{ pool: 'status' as const, carrier: null, entry: status, ability: definition!,
        abilityId: upper(itemField(definition, 'item_id')), order: 0, competes: false, statusId: upper(status.status_id) }];
    }).filter(supports);
    const due = (candidate: TriggerCandidate): boolean => triggerCall(() => gateTriggerCandidate(readers, candidate, owner, event, roller)).fire;
    if (![...candidates, ...heldModifiers].some(due)) return frame;
    if (rollerIsTarget === undefined) handBack('skill_roll_arr:incoming-roll-side-not-held');
    if (!ctx.members.has(ecId(owner)) || !ctx.members.has(ecId(roller))) handBack('skill_roll_arr:incoming-roll-member-not-held');
    for (const candidate of heldModifiers) {
      if (due(candidate)
          && consentAllows(ctx, owner, candidate.ability, candidate.abilityId, INCOMING_TRIGGER))
        frame.statuses.add(statusSourceId(candidate.entry));
    }
    const executable = candidates.filter(candidate => !(candidate.pool === 'status' && incomingModifierOnly(candidate.ability)));
    if (ctx.trigger || !executable.some(due)) return frame;
    const narrowed = { ...event, restrictToAbilityIds: [...new Set(executable.map(candidate => candidate.abilityId))] };
    const gap: TriggerGapFn = (current, candidate, triggerEvent) => {
      if (!supports(candidate) || candidate.pool === 'status' && incomingModifierOnly(candidate.ability)) return null;
      const type = String(itemField(candidate.ability, 'item_type') ?? '');
      return triggerExecutorGap(current, candidate.pool === 'status' && ['reaction_ability', 'free_ability'].includes(type)
        ? { ...candidate, pool: 'carrier', carrier: type as 'reaction_ability' | 'free_ability' } : candidate, triggerEvent);
    };
    triggerFamilyPreDraw(ctx, triggerOwner, narrowed, gap, roller);
    const addedBefore = ctx.statusesAdded.length;
    const dispatched = new Set<TriggerCandidate>();
    const fired = runTriggerFamily(ctx, narrowed, triggerOwner, roller, [owner], false, gap, false, (candidate, run) => {
      if (!supports(candidate) || candidate.pool === 'status' && incomingModifierOnly(candidate.ability)) return false;
      const before = ctx.rows.length;
      run();
      if (ctx.rows.length > before) dispatched.add(candidate);
      return true;
    });
    for (const candidate of fired) {
      if (!dispatched.has(candidate)) continue; // range/sight/target veto: no dispatch, spend or modifier
      if (candidate.pool === 'status') frame.statuses.add(upper(candidate.statusId));
      else frame.passives.add(upper(candidate.abilityId));
    }
    for (const status of ctx.statusesAdded.slice(addedBefore)) if (upper(status.target) === ecId(owner)) {
      frame.statuses.add(statusSourceId(status as unknown as Row));
      // A dispatched passive's own lasting payload is read from its new status, once.
      frame.passives.delete(upper(status.status_item_id));
    }
    return frame;
  } finally { ctx.read = previousRead; }
}
function guardRollLayers(ctx: JanusCtx, owner: Row, role: string, skillId: string | null,
  compiledAdvKnown: boolean, compiledAdvSources: ReadonlySet<string>, rollType: string, incoming: boolean,
  rollTypes: ReadonlySet<string> = new Set([rollType]), usedRollType: string | null = null, cmrtInert = false,
  skillNatural: { value: 1 | 20 | null } = { value: null },
  /** A STATUS-carried contesting_roll_{adv,dis}_vs_spells key this roll settles without the status (skill_roll_inc.php ~L965 reads them
   *  only off the ROLLER's compiled root): inert, exactly as the root / passive loops already treat them. Unsettled keeps the hand-back. */
  vsSpellsSettled: (key: string) => boolean = () => false,
  /** The roll facts a held next-roll-against rider status is gated on (nextRollAgainstRider); null = the rider keeps its hand-back. */
  nextAgainst: NextAgainstRoll | null = null, nextOwnConsumed: Row[] | null = null, opposing: Row | null = null,
  /** The caller's attack lane resolves the ROLLER's hit_to_crit itself (attack-roll.ts: a hit becomes a critical): a carrier of it is
   *  reported here instead of handed back. null = every other caller, which keeps the hand-back. */
  hitToCrit: { held: boolean } | null = null, incomingFrame: IncomingRollFrame | null = null,
  extremes: RollExtremes = { max: false, min: false }): number {
  // Root/passive forces are compiled. A held or newly applied status supplies its own skill list, independent of that compile.
  // The opposing party's skill forces are inert. As in resolve_caster_skill_forced_natural_main_roll, max wins across sources.
  const forcedSettled = (carrier: Row, key: string, status = false): boolean => {
    if (key !== 'skill_is_rolled_at_max' && key !== 'skill_is_rolled_at_min') return false;
    if (incoming) return true;
    const ids = ctx.read.entries(carrier, key).map(forcedEntryId);
    if (ids.some((id) => !/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id))) {
      if (status) handBack(`skill_roll_arr:unmodelled:${role}.status.${key}-entries`);
      return false;
    }
    if (role !== 'caster' || !skillId) return false;
    if (!ids.includes(upper(skillId))) return true;
    if (status) {
      if (key === 'skill_is_rolled_at_max' || skillNatural.value === 20) skillNatural.value = 20;
      else skillNatural.value = 1;
      return true;
    }
    return key === 'skill_is_rolled_at_max' ? skillNatural.value === 20 : skillNatural.value === 1 || skillNatural.value === 20;
  };
  // The CASTER's all-skill globals are counted once from the root (select-skill-for-contesting-roll.ts skillDetails, PHP's global
  // block) whatever carries them — a status, a passive, gear. A contestee's stay unmodelled.
  // The OPPOSING party's own skill layers (incoming = `to`) never reach the roller's roll: skill_roll_inc.php's target loop (~L705–840) and its
  // innate-passive walk read only roll_type_against_* and the roll-contingency triggers off $target_arr — the roller's all-skill ADV / DIS and
  // skill mods come from its OWN root. So an Exhausted (skill_rolls_dis) self-target contestee is inert on the `to` side (lane A4, R-100: the
  // "to death" Fortitude contest is self-targeted — the same PC is the roller and the `to`).
  // Ascension uses only the roller's compiled scalar below. Keep the separate
  // newly-triggered-status guards: those sources may not be in that compile yet.
  const counted = (key: string): boolean => key === 'ascension_skill_point_range_mod' || key === 'skill_roll_ignore_dis'
    || (role === 'caster' && (key === 'skill_rolls_adv' || key === 'skill_rolls_dis'))
    || (incoming && (key === 'skill_rolls_adv' || key === 'skill_rolls_dis' || key === 'skill_roll_mod'));
  // skill_roll_mod is per-skill: ft_collect_caster_skill_roll_mod_sources folds an entry only into the skill it names
  // (skill_roll_mod_skill_and_modifier — `skill`, else the list-entry uuid). A carrier whose entries all name OTHER skills
  // is inert for this roll (a "Skill Roll Mod: Excavating +5" status on an Initiative roll); one naming this skill still hands back.
  const otherSkillsOnly = (carrier: Row, key: string): boolean => {
    if (key !== 'skill_roll_mod' || !skillId) return false;
    const entries = ctx.read.entries(carrier, key);
    return entries.length > 0 && entries.every((entry) => {
      const id = upper(record(entry)?.skill ?? layerListEntryUuid(entry));
      return /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(id) && id !== skillId;
    });
  };
  const attackTierInert = ![...rollTypes].some((t) => ATTACK_ROLL_TYPE_IDS.has(t));
  const inertAttackTier = (key: string): boolean => attackTierInert && ATTACK_TIER_LAYERS.includes(key);
  // A roller holding hit_to_crit (root grant, held status, passive): "Any Regular Hit Becomes a Critical Hit" — the attack lane that asked
  // for it applies it after the d20. Evaluated last in each guard below, so it judges only a carrier that really carries the layer.
  //  - a STATUS_EFFECT carrier (Paralyzed) means hits AGAINST its holder crit: never the roller's own grant (inert here);
  //  - check_ability_conditions()' target gates (Melting Judgement: if_target_has_status_effect_active Corroding) are cast-time gates the
  //    compile never reads, so they are judged against THIS roll's opposing creature; an unmet gate grants nothing;
  //  - a HELD status's other conditions are judged against the current frame; a bucket passive already has its compile verdict.
  const rollerHitToCrit = (key: string, carrier: Row, kind: 'root' | 'status' | 'passive'): boolean => {
    if (key !== 'hit_to_crit') return false;
    if (incoming && kind === 'status' && targetIsAutoCriticalForAttackRoll(carrier, rollType, browserDependencies({
      has_status_effect: (_row: unknown, id: string) => upper(itemField(carrier, 'item_id')) === id,
      layer_has: (row: Row, name: string) => ctx.read.has(row, name),
      layer_value: (row: Row, name: string) => ctx.read.value(row, name),
    }))) return true;
    if (role !== 'caster' || hitToCrit === null) return false;
    if (kind !== 'root' && String(itemField(carrier, 'item_type') ?? '') === 'status_effect') return true;
    const keys = Object.keys(record(carrier.item_layer_types) ?? {});
    if (kind === 'status' && keys.some((k) => isAbilityConditionKey(k) && !HIT_TO_CRIT_TARGET_GATES.includes(k))
        && !heldRollConditionsMet(ctx, carrier, owner)) return true;
    if (kind !== 'root' && HIT_TO_CRIT_TARGET_GATES.some((k) => keys.includes(k))) {
      if (!opposing) handBack('skill_roll_arr:unmodelled:caster.hit_to_crit-target-gate-without-target');
      if (!targetStatusGatesMet(ctx, carrier, opposing!, 'skill_roll_arr:hit_to_crit')) return true;
    }
    hitToCrit.held = true;
    return true;
  };
  const extremeTypes = new Set(usedRollType ? [usedRollType] : nextAgainst?.usedRollType ? [nextAgainst.usedRollType] : []);
  const mergeExtremes = (carrier: Row): void => {
    const prefix = incoming ? 'roll_type_against_' : 'roll_type_';
    if (extremeTypes.size) for (const axis of ['max', 'min']) {
      const key = `${prefix}is_rolled_at_${axis}`;
      if (ctx.read.has(carrier, key) && (!ctx.read.entries(carrier, key).length
          || ctx.read.entries(carrier, key).some(entry => !/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(forcedEntryId(entry)))))
        handBack(`skill_roll_arr:invalid:${role}.${key}-entries`);
    }
    extremes.max ||= carrierNamesRollType(ctx, carrier, `${prefix}is_rolled_at_max`, extremeTypes);
    extremes.min ||= carrierNamesRollType(ctx, carrier, `${prefix}is_rolled_at_min`, extremeTypes);
  };
  const incomingAllows = (definition: Row, status: Row | null): boolean => {
    if (!incoming) return true;
    if (status && ctx.read.has(definition, 'target_marked_by_caster')
        && upper(ctx.read.value(status, 'status_caster_id') ?? status.caster) !== ecId(opposing)) return false;
    if (ctx.read.has(definition, INCOMING_TRIGGER) && !isPostRollReactionStatus(ctx.read, definition))
      return !!(status ? incomingFrame?.statuses.has(statusSourceId(status))
        : incomingFrame?.passives.has(upper(itemField(definition, 'item_id'))));
    return creatureTypeGateAllows(ctx, definition, opposing);
  };
  mergeExtremes(owner);
  let delta = rollTypeDelta(ctx, owner, rollType, incoming, role);
  // The owner's OWN root layers only: the layer reader also walks the owner's carriers, and a carried passive's grant is vetted against
  // the compiled sources by the passive loop below, under the passive's own id (Session 1 step 6 — a PC's Ogrefolk Origins Willpower DIS
  // reached this owner check through the carrier walk and could never match the owner's id).
  const rootLayers = record(owner.item_layer_types) ?? {};
  const rootNames = (key: string): boolean => !!skillId && (Array.isArray(rootLayers[key]) ? rootLayers[key] as unknown[] : rootLayers[key] == null ? [] : [rootLayers[key]])
    .some((entry) => String(itemField(record(entry), 'item_id') ?? entry).toUpperCase() === skillId);
  const scopedDelta = (carrier: Row, identities: unknown[]): number => {
    if (!hasSkillScopedModifier(ctx, carrier, skillId)) return 0;
    if (!compiledAdvKnown) handBack('skill_roll_arr:compiled-adv-not-verified');
    return identities.some(id => compiledAdvSources.has(upper(id))) ? 0 : scopedSkillDelta(ctx, carrier, skillId);
  };
  if (rootNames('skill_roll_adv') || rootNames('skill_roll_dis'))
    delta += scopedDelta({ item_layer_types: rootLayers }, [itemField(owner, 'item_id'), actorIdOf(owner)]);
  for (const key of ROLL_LAYERS) if (!['roll_type_adv', 'roll_type_dis', 'roll_type_against_adv', 'roll_type_against_dis'].includes(key)
    && !ROLL_TYPE_EXTREME_KEYS.has(key) && !counted(key) && !VS_SPELLS.includes(key) && !inertAttackTier(key) && !otherSkillsOnly(owner, key) && ctx.read.has(owner, key)
    && !forcedSettled(owner, key) && !rollerHitToCrit(key, owner, 'root')) handBack(`skill_roll_arr:unmodelled:${role}.${key}`);
  for (const key of ROLL_TRIGGERS) if (!(cmrtInert && key === CMRT_TRIGGER) && ctx.read.has(owner, key)) handBack(`skill_roll_arr:unmodelled:${role}.${key}`);
  // Legacy roll_type_modifiers is a cached source projection. The native walk below reads its held sources, once.
  const consumed: Row[] = [];
  for (const status of activeStatuses(ctx, owner)) {
    // Cached status_arr can be an identity-only shell; prefer the full catalog definition.
    const definition = ctx.read.definition(String(status.status_item_id ?? '')) ?? record(status.status_arr);
    if (!definition) handBack(`skill_roll_arr:unmodelled:${role}.status-definition`);
    if (!incomingAllows(definition!, status)) continue;
    mergeExtremes(definition!);
    const againstRider = nextAgainst && (role === 'caster' || role === 'target')
      ? nextRollAgainstRider(ctx.read, definition, role, rollTypes, nextAgainst,
        incoming || nextAgainst.rollerIsTarget === false && ctx.read.has(definition, INCOMING_TRIGGER)) : null;
    const ownRider = !againstRider && nextAgainst && role === 'caster'
      ? nextRollRider(ctx.read, definition, role, rollTypes, nextAgainst, 'triggers_on_next_roll_type') : null;
    const rider = againstRider ?? ownRider;
    if (rider) {
      delta += rider.delta;
      if (rider.consume) (ownRider && nextOwnConsumed ? nextOwnConsumed : consumed).push(status as unknown as Row);
      continue;
    }
    // A fresh held status can predate the known compile. Its source identity is the stored status row.
    if (hasSkillScopedModifier(ctx, definition, skillId) && (!compiledAdvKnown || !compiledAdvSources.has(upper(status.status_id)))) {
      if (role !== 'caster' || !compiledAdvKnown || Number(status.status_paused ?? 0) !== 0) handBack('skill_roll_arr:compiled-adv-not-verified');
      delta += scopedSkillDelta(ctx, definition, skillId);
    }
    delta += rollTypeDelta(ctx, definition, rollType, incoming, `${role}.status`,
      incoming ? new Set([...rollTypes, SKILL_ROLL_ALIAS]) : undefined, { opposing });
    for (const key of ROLL_LAYERS) if (!['roll_type_adv', 'roll_type_dis', 'roll_type_against_adv', 'roll_type_against_dis'].includes(key)
      && !ROLL_TYPE_EXTREME_KEYS.has(key) && !counted(key) && !(VS_SPELLS.includes(key) && vsSpellsSettled(key)) && !inertAttackTier(key) && !otherSkillsOnly(definition, key) && ctx.read.has(definition, key)
      && !forcedSettled(definition, key, true) && !rollerHitToCrit(key, definition, 'status'))
      handBack(`skill_roll_arr:unmodelled:${role}.status.${key}`);
    // skill_roll_arr's caster TRIGGER loop gate (skill_roll_status_trigger_gates_pass): a next-roll carrier is inert when the used
    // item carries no roll_type (a trap check) — no ADV from it, never consumed. Its CMRT half is the status-pool dispatch.
    const inertNextRoll = role === 'caster' && usedRollType === '' && NEXT_ROLL_TRIGGERS.some((k) => ctx.read.has(definition, k));
    for (const key of ROLL_TRIGGERS) {
      if (!ctx.read.has(definition, key)) continue;
      // The mark scopes incoming effects through incomingAllows; it is not an own-roll trigger.
      if (key === 'target_marked_by_caster') continue;
      if (inertNextRoll && (NEXT_ROLL_TRIGGERS.includes(key) || key === CMRT_TRIGGER)) continue;
      if (cmrtInert && key === CMRT_TRIGGER) continue;
      // A target's OWN next-roll rider rides the next roll IT makes (its caster pass reads it then — on a self roll, the caster pass above),
      // never a roll made against it: the passive loop's nextRollInertHere twin (FT3D 2026-10-16, Priest Guy's Attention under Steady Bending).
      // Not when the status also carries against axes: legacy's target gate (skill_roll_status_trigger_gates) can drop their push by this key.
      if (role === 'target' && key === 'triggers_on_next_roll_type'
          && !['roll_type_against_adv', 'roll_type_against_dis'].some((k) => ctx.read.has(definition, k))) continue;
      // Fortunate triggers on the next ordinary Skill Roll. Contest rolls use
      // a different roll_type; a pending Fortunate is inert for this roll.
      if (key === 'triggers_on_next_roll_type'
          && isPendingThisDispatch(ctx, status)
          && upper(status.status_item_id) === 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8'
          && !ctx.read.entries(definition, key).some((entry) => upper(itemField(record(entry), 'item_id') ?? entry) === rollType)) continue;
      handBack(`skill_roll_arr:unmodelled:${role}.status.${key}`);
    }
  }
  consumeNextRollStatuses(ctx, owner, consumed);
  const compiled = record(record(record(owner.computed)?.creature)?.abilities)?.passive_ability;
  if (compiled != null && !Array.isArray(compiled)) handBack('skill_roll_arr:passive-carriers-not-held');
  // layer_entries() gives the compiled carrier precedence over its root grant.
  // Walking both representations applies one passive twice.
  for (const entry of compiled ?? ctx.read.entries(owner, 'passive_ability')) {
    const passive = record(entry);
    if (passive?.passive_conditions_active === false) continue;
    const definition = carrierDefinition(ctx, entry);
    if (!definition) handBack(`skill_roll_arr:unmodelled:${role}.passive-definition`);
    if ((incoming && passive?.built_from_item_type === 'status_effect') || !incomingAllows(definition, null)) continue;
    mergeExtremes(definition);
    delta += scopedDelta(definition, [layerListEntryUuid(entry), itemField(definition, 'item_id'), layerListEntryUuid(passive?.source)]);
    delta += rollTypeDelta(ctx, definition, rollType, incoming, `${role}.passive`,
      incoming ? new Set([...rollTypes, SKILL_ROLL_ALIAS]) : undefined,
      { opposing, targetStatus: !incoming, ...(typeof entry === 'string' ? { holder: owner } : {}) });
    for (const key of ROLL_LAYERS) if (!['roll_type_adv', 'roll_type_dis', 'roll_type_against_adv', 'roll_type_against_dis'].includes(key)
      && !ROLL_TYPE_EXTREME_KEYS.has(key) && !counted(key) && !VS_SPELLS.includes(key) && !inertAttackTier(key) && !otherSkillsOnly(definition, key) && ctx.read.has(definition, key)
      && !forcedSettled(definition, key) && !rollerHitToCrit(key, definition, 'passive')) handBack(`skill_roll_arr:unmodelled:${role}.passive.${key}`);
    // CMRT is dispatched below through janus_trigger's shared predicate/executor, including hit/critical gates. The passive's own
    // next-roll key describes its granted STATUS, consumed above; it never consumes or blocks the permanent source bucket.
    // Against-roll passive narrowing is retained; the opposing party's next-roll keys are never read by this roll.
    const nextRollInertHere = (key: string): boolean => {
      if (key === 'triggers_on_next_roll_type') return true;
      if (role === 'target' && NEXT_ROLL_TRIGGERS.includes(key)) return true;
      const listed = NEXT_ROLL_TRIGGERS.includes(key) ? ctx.read.entries(definition, key).map((e) => upper(layerListEntryUuid(e))) : [];
      return listed.length > 0 && listed.every((t) => /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(t)
        && !rollTypes.has(t) && t !== SKILL_ROLL_ALIAS);
    };
    for (const key of ROLL_TRIGGERS) if (key !== CMRT_TRIGGER && !nextRollInertHere(key) && ctx.read.has(definition, key))
      handBack(`skill_roll_arr:unmodelled:${role}.passive.${key}`);
  }
  return delta;
}

/** Read every queued rider before removing connected peers; a peer already consumed by a prior removal is skipped. */
function consumeNextRollStatuses(ctx: JanusCtx, owner: Row, statuses: readonly Row[]): void {
  for (const status of statuses)
    if (!ctx.statusesRemoved.some((removed) => upper(removed.status_id) === upper(status.status_id) && upper(status.status_id)))
      removeStoredStatus(ctx, owner, status, 'single');
}

/** includes/functions/skill_roll_inc.php:skill_roll_arr */
export function skillRollIgnoreDisPreflight(ctx: JanusCtx, creature: Row, skillId: string): void {
  const key = 'skill_roll_ignore_dis';
  const matches = (provider: Row): boolean => ctx.read.entries(provider, key).some(entry => {
    const id = layerListEntryUuid(entry);
    // A variable-name reference needs PHP's Skill catalog resolution, before the draw.
    if (!/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id)) handBack('skill_roll_arr:ignore-dis-skill-unproved');
    return id === upper(skillId);
  });
  const read = { ...ctx.read, resolveItem: (entry: unknown) => ctx.read.definition(layerListEntryUuid(record(entry)?.item_arr ?? entry)) } as unknown as AbilityPreconditionReaders;
  const provider = casterLayerProvider(read, creature, key, matches);
  if (provider === undefined) handBack('skill_roll_arr:ignore-dis-provider-unproved');
  // Net stacks cannot reveal a DIS source cancelled by ADV. Conservatively hand back every matching roll.
  if (provider || activeStatuses(ctx, creature).some(status => {
    const definition = ctx.read.definition(upper(status.status_item_id)) ?? record(status.status_arr);
    if (!definition) handBack('skill_roll_arr:ignore-dis-status-unproved');
    return matches(definition);
  })) handBack('skill_roll_arr:unmodelled:caster.skill_roll_ignore_dis');
}

export function skillRollArr(ctx: JanusCtx, input: JanusInput, creature: Row, skill: SelectedSkill, to: Row | null, dc: number,
  options?: { rollType?: string; onNatural?: (natural: number) => void;
    /** The selected weapon's already-materialized enchantment modifier. */
    weaponAttackMod?: number;
    /** Display-only snapshot, persisted by the existing scalar die-face metadata lane. */
    coverTier?: CoverTier;
    /** The attack's resolved natural-face threshold, recorded on the kept/discarded d20s. */
    criticalThreshold?: number;
    /** The LOGGED roll_type_1 when it is not `rollType` — skill_roll_arr's `layer_value(item_id_used_arr, 'roll_type') ?? item_type`: initiative
     *  logs 'innate_skill' while its effective roll types are the generic skill_roll alias only (roll_initiative_for_caster; lane A4). */
    rowType?: string;
    /** The row's roll_item_id when it is not the used item's id — counterspell_check passes a discern_item_id() array with no
     *  item_id_used, so PHP logs the watcher's contest row with an empty item (lane RX #2). */
    rollItemId?: string | null;
    /** The roller is the use's TARGET (a contestee / a save), read from its own target array in PHP. */
    asTarget?: boolean;
    /** The roll's side, when explicitly declared by its caller (including concentration's own-roll false). Otherwise the current
     *  input's actor/targets and opposing row identify it; an ambiguous side is refused only when a side-dependent rule is due. */
    isTarget?: boolean;
    /** The used item's roll_type when the catalog definition does not carry it (a natural weapon: the attack phase supplies the melee roll
     *  type on its runtime item_arr). */
    usedRollType?: string;
    /** A campaign house rule's own roll (the Maimed-on-crit save, lane A8): logged under its banner, and the roller's roll-type ADV / DIS is read
     *  over EVERY effective roll type (the used item's and the skill_roll alias too — rollTypeModifierStacks), as skill_roll_arr does. */
    houseRule?: CritHouseRule;
    /** A CALLER-forced natural (check_concentration: concentration_roll_is_rolled_at_max / _at_min, 2026-10-07 — legacy copy R-145):
     *  the d20 is not drawn; deterministic like the roll-type force (no luck gain / loss, no ascension check). A compiled skill-keyed
     *  forced natural wins over it, as in skill_roll_inc.php. */
    forcedNatural?: 1 | 20 | null;
    /** PHP skill_roll_arr()'s fire_caster_roll_type_triggers: false (MV8, the stealth awareness contest — passive perception, not either
     *  creature acting): NO triggers_when_caster_makes_roll_type dispatch for this roll (the Hidden status's own Hidden Reveal listens for
     *  Contesting Roll); next-roll consumption still runs. Absent = every existing caller, unchanged. */
    fireCasterRollTypeTriggers?: false;
    /** The spell attack lane (attack-roll.ts) turns a hit into a critical when the ROLLER holds hit_to_crit: guardRollLayers sets `held`
     *  instead of handing the carrier back. Absent = every other caller, unchanged. */
    hitToCrit?: { held: boolean } }): number {
  skillRollIgnoreDisPreflight(ctx, creature, skill.id);
  if (!Number.isSafeInteger(dc) || !Number.isSafeInteger(skill.adv) || !Number.isSafeInteger(skill.modifier)
      || !Number.isSafeInteger(skill.tier)) handBack('skill_roll_arr:invalid-numeric-state');
  const rollType = upper(options?.rollType ?? CONTEST_ROLL_ID);
  // PHP keys the forced natural on the USED item's roll_type; include it beside this roll's own type (a superset — safe).
  const usedRollType = upper(options?.usedRollType ?? ctx.read.value(ctx.read.definition(input.abilityId), 'roll_type'));
  const rollTypes = new Set([rollType, usedRollType].filter(Boolean));
  const rollerId = ecId(creature), frameCaster = upper(input.casterId);
  const ownFrame = !!rollerId && rollerId === frameCaster;
  const targetFrame = !!rollerId && input.targetIds.some(id => upper(id) === rollerId)
    && !ownFrame && (options?.asTarget === true || !!to && ecId(to) === frameCaster);
  const rollerIsTarget = options?.isTarget ?? (targetFrame ? true : ownFrame && !options?.asTarget ? false : undefined);
  const weaponAttackMod = options?.weaponAttackMod ?? 0;
  if (!Number.isSafeInteger(weaponAttackMod)) handBack('skill_roll_arr:invalid-weapon-attack-mod');
  const manual = applyRawDiceModifiers(ownFrame && rollerIsTarget !== true && !options?.asTarget ? input : {}, skill.modifier + weaponAttackMod, skill.adv);
  skill.adv = manual.stacks;
  const extremes: RollExtremes = { max: false, min: false };
  const incomingFrame = incomingRollFrame(ctx, to, creature, rollType, rollerIsTarget);
  // A Fortunate THIS dispatch granted (an earlier natural-20 luck gain in the same use): skill_roll_arr always adds the `skill_roll`
  // alias to the roll's effective roll types, so Fortunate's roll_type_adv [Skill Roll] is +1 ADV on the creature's NEXT skill roll of
  // ANY roll type, and its triggers_on_next_roll_type consumes it before the dice — the status never persists. Counterspell oracle
  // (lane RX #2, 2026-09-28): a caster's natural-20 counter contest, then its spell attack / contest roll (AO-41, AO-155 -counter).
  // Only the USE'S CASTER sees it: PHP refreshes that one live $caster_arr; the same creature rolling as a contestee reads its separate
  // target array, where the pending Fortunate is inert and persists (AO-158-counter: a self-target Frostbite). A trap check (no used
  // roll_type) keeps its inert next-roll rule below.
  const pendingFortunate = usedRollType === '' || options?.asTarget || actorIdOf(creature) !== upper(input.casterId) ? undefined : ctx.statusesAdded.find((status) =>
    upper(status.status_item_id) === FORTUNATE_ID && upper(status.target) === actorIdOf(creature) && isPendingThisDispatch(ctx, status));
  if (pendingFortunate) { ctx.statusesAdded.splice(ctx.statusesAdded.indexOf(pendingFortunate), 1); skill.adv += 1; }
  // ft_stealth_apply_revealed_attack_modifiers: the use's FIRST attack roll keeps the ADV / DIS of the Hidden / Invisible its reveal just
  // ended (stealth-reveal-carry.ts) — a roller's own roll only.
  if (!options?.asTarget) skill.adv += takeRevealedAttackAdv(ctx, creature, rollType, usedRollType);
  // skill_roll_inc.php ~L406: the CMRT fire needs a UUID roll type — roll_type_1 (here the logged row type), else the used item's roll_type. A
  // roll with neither (initiative: 'innate_skill' and no item roll_type) fires NO triggers_when_caster_makes_roll_type carrier, so one is
  // inert for it (lane A4 for N5b). Every other caller logs a UUID type: unchanged.
  const cmrtInert = options?.fireCasterRollTypeTriggers === false || options?.rowType !== undefined && !/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(options.rowType) && usedRollType === '';
  const usedItem = ctx.read.definition(input.abilityId);
  // the used item is the rolled SKILL itself (initiative, a plain skill check) when its definition is not held: never a spell contest
  const vsSpellsInert = usedItem ? !(String(itemField(usedItem, 'item_type') ?? '') === 'spell' && ctx.read.has(usedItem, 'skill_roll_contestee'))
    : !!skill.id && upper(input.abilityId) === upper(skill.id);
  // The ROLLER's held status carrying a vs-spells key (FT3D 2026-10-16: Wizard Guy under Paladin Guy's Null Sanctum casting Dominate
  // Reaction): PHP reads the key only on the roller's $is_target roll, off its compiled root, one stack (counted below). Settled when the
  // contest is not a spell's, on the roller's OWN roll (isTarget false), or when the compiled root already carries the key. A $is_target roll
  // whose root lacks it (a compile that predates the status) or an unstated side keeps the hand-back, as the root read below does.
  const rollerVsSpellsSettled = (key: string): boolean => vsSpellsInert || rollerIsTarget === false
    || (rollerIsTarget === true && vsSpellsFlagSet(ctx.read.value(creature, key)));
  const nextAgainst: NextAgainstRoll = { usedRollType, rollerIsTarget };
  const nextOwnConsumed: Row[] = [];
  const skillNatural = { value: skill.forcedNatural ?? null };
  skill.adv += guardRollLayers(ctx, creature, 'caster', skill.id, skill.compiledAdvKnown, skill.compiledAdvSources, rollType, false, rollTypes, usedRollType, cmrtInert,
    skillNatural, rollerVsSpellsSettled, nextAgainst, nextOwnConsumed, to, options?.hitToCrit ?? null, null, extremes);
  const addedBefore = ctx.statusesAdded.length;
  const cmrtSuppressed = options?.fireCasterRollTypeTriggers === false;
  if (usedRollType === '') skill.adv += frontEndNextRollCheck(ctx, creature, rollType, to, skill.id, cmrtSuppressed);
  else if (!cmrtSuppressed) fireCasterMakesRollType(ctx, creature, rollType, to);
  // A fired own next-roll carrier lands on this roll and is used up through the same rider/removal path as a held one.
  if (usedRollType !== '')
    skill.adv += consumeCmrtNextRollStatuses(ctx, creature, addedBefore, usedRollType, skill.id, rollTypes);
  guardCmrtAddedStatuses(ctx, creature, to, addedBefore, usedRollType, skill.id, rollTypes);
  // Legacy fires CMRT before retiring an own-roll status: a source's "while not holding this buff" condition must still see it.
  // Retire earlier pending rows after the new-status walk too, preserving that walk's pre-fire index.
  consumeNextRollStatuses(ctx, creature, nextOwnConsumed);
  // skill_roll_inc.php ~L374–388 + L454–537 (lane A8): the roll's EFFECTIVE roll types — roll_type_1, the used item's roll_type, the skill_roll
  // alias. A roller making a TARGET roll ($is_target) also takes its OWN roll_type_against_adv / _dis sources for them (a held Evade: DIS on its
  // own save against an attack's effect). guardRollLayers above counts roll_type_1's own ADV / DIS; a house-rule roll counts the rest too.
  {
    const rowType = upper(options?.rowType ?? rollType);
    const effective = [rowType, usedRollType, SKILL_ROLL_ALIAS];
    const scope = { usedItemType: String(itemField(ctx.read.definition(input.abilityId), 'item_type') ?? ''), usedRollType, rollTo: to ? actorIdOf(to) : '', opposing: to };
    if (rollerIsTarget === true) skill.adv += rollTypeModifierStacks(ctx, creature, effective, ['against_adv', 'against_dis'], scope, null, true);
    if (options?.houseRule) {
      skill.adv += rollTypeModifierStacks(ctx, creature, effective.filter((t) => upper(t) !== rollType), ['adv', 'dis'], scope);
      // roll_type_1's own adv / dis from the buckets guardRollLayers does not walk (Codex A8 r1 P2)
      skill.adv += rollTypeModifierStacks(ctx, creature, [rollType], ['adv', 'dis'], scope, new Set(['feat', 'reaction_ability', 'inventory', 'enchantment']));
    } else skill.adv += rollTypeModifierStacks(ctx, creature, effective, ['adv', 'dis'], scope, new Set(['inventory', 'enchantment']));
  }
  // the OPPOSING party's vs-spells keys never reach this roll: PHP reads them off the roller ($caster_arr) only
  if (to) skill.adv += guardRollLayers(ctx, to, 'target', null, false, new Set(), rollType, true, rollTypes, null, false, skillNatural, () => true,
    nextAgainst, null, creature, null, incomingFrame, extremes);
  const ability = ctx.read.definition(input.abilityId);
  if (!ability) definitionNotHeld('skill_roll_arr:ability-definition-not-held', input.abilityId);
  // skill_roll_inc.php:1020: a combat target resisting an authored Charmed application receives one ADV stack.
  if (rollerIsTarget === true && ctx.read.inCombat(ctx.encounter, creature)
      && ctx.read.entries(ability, 'status_effect_applied_to_target_after_contesting_roll_failed')
        .some(entry => upper(layerListEntryUuid(entry)) === 'DD4C0995-C3D2-43A3-CFC7-226C3005274C')) skill.adv += 1;
  // ft_apply_spell_circle_contest_dis() (spell_circle_contest_dis.php, skill_roll_inc.php ~L1001 inside the $is_target block): a contestee's roll
  // against a SPELL carrying skill_roll_contestee takes ONE DIS stack when the SPELLCASTER (roll_to) carries target_contesting_roll_dis_vs_spell_circle
  // naming one of the spell's circles — on any provider (find_caster_layer_provider_arrs: the creature, its ability buckets, its feats and ONE level
  // of their grants) or applied status. Ported 2026-10-13 (Fireball playtest follow-up): it used to hand back on any presence, including a bare
  // text match on the caster row. A grant-level if_* condition (check_ability_conditions) is not modelled → named hand-back.
  if (to && rollerIsTarget !== false && String(itemField(ability, 'item_type') ?? '') === 'spell' && ctx.read.has(ability, 'skill_roll_contestee')
      && spellCircleContestDisMatched(ctx, to, ability)) {
    if (rollerIsTarget === undefined) handBack('skill_roll_arr:vs-spells-side-unknown:target_contesting_roll_dis_vs_spell_circle');
    if (rollerIsTarget) skill.adv -= 1;
  }
  for (const key of [...ROLL_LAYERS, ...ROLL_TRIGGERS])
    // These authored grant/next-roll layers are not sources on the current
    // skill roll. PHP reads the caster's compiled sources at this phase. The against-rider (roll_type_against_* +
    // triggers_on_next_roll_type_against: Guiding Bolt's on-hit status) is read only off the TARGET's status rows, never off $item_id_used_arr.
    // target_marked_by_caster scopes that applied status to its applier (incomingAllows above); the cast creates the mark after winning
    // its contest. It does not require a pre-existing mark, including for a first Peek in Bags inspection.
    if (!['roll_type_dis', 'skill_rolls_dis', 'triggers_on_next_roll_type', 'target_marked_by_caster', ...AGAINST_RIDER].includes(key) && ctx.read.has(ability, key))
      handBack(`skill_roll_arr:unmodelled:ability.${key}`);
  for (const suffix of ['adv', 'dis']) {
    const key = `contesting_roll_${suffix}_vs_status_effect`;
    // R-196 (2026-10-07): the roller's own layer read IS PHP's `layer_entries($caster_arr, $caster_roll_key)` — ftLayerEntries bridges
    // computed.creature.defenses (FT_CREATURE_DEFENSE_SLOTS), the status-folded root residue (an aura occupant's DIS / ADV vs a status,
    // R-140) and the carrier walk, deduped by uuid. It used to hand back on any match; it now stacks exactly as skill_roll_inc.php ~L928:
    // one stack per applied-on-failure entry of THIS roller's side per matching id. The projection loop below then only runs for a read
    // that came back empty (a reader without the defense bridge).
    const ownIds = ctx.read.entries(creature, key).map((entry) => upper(layerListEntryUuid(entry))).filter(Boolean);
    if (ownIds.length) {
      if (rollerIsTarget === undefined && ['status_effect_applied_to_caster_after_contesting_roll_failed',
        'status_effect_applied_to_target_after_contesting_roll_failed'].some((layer) =>
        ctx.read.entries(ability, layer).some((entry) => ownIds.includes(upper(layerListEntryUuid(entry))))))
        handBack(`skill_roll_arr:contest-side-not-held:${key}`);
      const sideKey = rollerIsTarget ? 'status_effect_applied_to_target_after_contesting_roll_failed'
        : 'status_effect_applied_to_caster_after_contesting_roll_failed';
      for (const applied of ctx.read.entries(ability, sideKey)) {
        const appliedId = upper(layerListEntryUuid(applied));
        if (!appliedId) continue;
        for (const id of ownIds) if (id === appliedId) skill.adv += suffix === 'adv' ? 1 : -1;
      }
      continue;
    }
    // skill_roll_inc.php ~L928 through the COMPILED defense store (compute_defenses() folds a carrier's contesting_roll_*_vs_status_effect
    // into computed.creature.defenses — the layer reader above does not see it): one stack per matching entry against the ability's
    // applied-on-failure statuses for THIS roller's side (the target's list for a contestee, the caster's list for the caster). Session 1
    // step 6, 2026-09-29 — a PC's Iron Resolve (ADV vs Frightened) was silently dropped, so Fleshdobbin Shriek drew a DIS d20 PHP never rolled.
    const projected = record(record(record(creature.computed)?.creature)?.defenses)?.[key];
    if (projected != null && !Array.isArray(projected)) handBack(`skill_roll_arr:defense-projection-not-held:${key}`);
    const projectedIds = (Array.isArray(projected) ? projected : []).map(entry => upper(layerListEntryUuid(entry))).filter(Boolean);
    if (projectedIds.length) {
      if (rollerIsTarget === undefined && ['status_effect_applied_to_target_after_contesting_roll_failed', 'status_effect_applied_to_caster_after_contesting_roll_failed']
        .some((layer) => ctx.read.entries(ability, layer).some((entry) => projectedIds.includes(upper(layerListEntryUuid(entry))))))
        handBack(`skill_roll_arr:contest-side-not-held:${key}`);
      const appliedKey = rollerIsTarget ? 'status_effect_applied_to_target_after_contesting_roll_failed'
        : 'status_effect_applied_to_caster_after_contesting_roll_failed';
      for (const applied of ctx.read.entries(ability, appliedKey)) {
        const appliedId = upper(layerListEntryUuid(applied));
        if (!appliedId) continue;
        for (const id of projectedIds) if (id === appliedId) skill.adv += suffix === 'adv' ? 1 : -1;
      }
    }
  }
  // skill_roll_inc.php ~L965: a contest against a SPELL carrying skill_roll_contestee — the roller's scalar contesting_roll_adv_vs_spells /
  // _dis_vs_spells (layer_value, not empty) add ONE stack each, only when PHP's $is_target holds. A caller whose PHP side is not
  // stated hands back when the flag is present (Session 1 step 6, 2026-09-29 — the RX reactor capture's Spell Resistance cases).
  if (String(itemField(ability, 'item_type') ?? '') === 'spell' && ctx.read.has(ability, 'skill_roll_contestee')) {
    for (const [key, step] of [['contesting_roll_adv_vs_spells', 1], ['contesting_roll_dis_vs_spells', -1]] as const) {
      if (!vsSpellsFlagSet(ctx.read.value(creature, key))) continue;
      if (rollerIsTarget === undefined) handBack(`skill_roll_arr:vs-spells-side-unknown:${key}`);
      if (rollerIsTarget) skill.adv += step;
    }
  }
  if (ctx.read.has(ability, 'weapon_attack_mod') && options?.weaponAttackMod === undefined)
    handBack('skill_roll_arr:unmodelled:ability.weapon_attack_mod');
  // caster_is_unlucky() spends -1 luck before the d20, adds one DIS stack,
  // and logs the spend before the skill's own roll row.
  const inCombat = ctx.read.inCombat(ctx.encounter, creature);
  // The compiled encounter row omits a stored zero in_combat in its lean shape.
  // A complete row identity plus current_round proves this is that row, rather than
  // an arbitrary context shell; the reader's inCombat mirrors PHP's membership test.
  const heldLuck = ecLean(creature, 'luck');
  const luck = heldLuck == null ? null : requiredInt(heldLuck, 'skill_roll_arr:invalid-luck');
  const encounterState = record(ctx.encounter.encounters);
  if (luck === -1 && encounterState?.in_combat == null && !upper(encounterState?.active_combat_id)
      && (!upper(encounterState?.encounter_id) || !encounterState || !Object.prototype.hasOwnProperty.call(encounterState, 'current_round')))
    handBack('skill_roll_arr:combat-context-not-held-for-luck');
  if (inCombat && luck == null) handBack('skill_roll_arr:luck-not-held');
  if (inCombat && luck === -1) {
    skill.adv -= 1;
    ec(creature).luck = 0;
    insertRoll(ctx, { roll_type_1: 'unlucky_roll', by: creature });
  }
  // attacks_against_cannot_gain_adv (Psion Clairvoyant L25 "Foreseen Assault", 2026-10-02): an ATTACK roll against a `to` carrying
  // it cannot end above the attacker's positive manual stacks — the last adjustment before the d20 (legacy copy R-135).
  if (to && options?.asTarget !== true && rollerIsTarget !== true && [...rollTypes].some((t) => ATTACK_ROLL_TYPE_IDS.has(t))
      && (flagSet(ctx.read.value(to, ATTACKS_AGAINST_CANNOT_GAIN_ADV)) || rootFlagSet(to, ATTACKS_AGAINST_CANNOT_GAIN_ADV)))
    skill.adv = clampAttackAdvAgainst(skill.adv, manual.manualStacks);
  const ascensionThreshold = skillAscensionThreshold(ctx, creature, skill);
  // skill_roll_inc.php ~L1163: a compiled or status skill force SETS the natural — no d20 is drawn and ADV /
  // DIS has nothing to pick from (the row still logs the net stacks); the display keeps PHP's one tuple. Luck gain / loss and the ascension
  // check run as for any natural 20 / 1. Caller- and roll-type-forced naturals skip those rewards.
  const deterministicNatural = skillNatural.value === null
    ? options?.forcedNatural ?? (extremes.max ? 20 : extremes.min ? 1 : null) : null;
  const forced = skillNatural.value ?? deterministicNatural;
  const main: { total: number; faces: unknown[] } = forced !== null
    ? { total: forced, faces: [[forced, `d20 forced natural ${forced}`]] }
    : rollDiceFaces(ctx, 1, 20, skill.adv);
  const kept = main.total;
  options?.onNatural?.(kept);
  const tierDie = skill.tier > 0 ? skill.tier + 3 : 0;
  const tierBonus = tierDie ? ctx.roll.d(tierDie) : 0;
  const fullFaces: unknown[] = [...main.faces];
  if (tierDie) fullFaces.push([tierBonus, `Skill Roll d${tierDie}`]);
  fullFaces.push(...manual.faces);
  if (skill.allSkillsMod) fullFaces.push([skill.allSkillsMod, 'All Skills Mod']);
  if (skill.skillRollMod) fullFaces.push([skill.skillRollMod, 'Skill Roll Mod']);
  if (weaponAttackMod) fullFaces.push([weaponAttackMod, 'Weapon Attack Mod']);
  fullFaces.push([skill.essenceMod, `${skill.essenceName} Essence`]);
  const result = kept + manual.modifier + tierBonus;
  if (inCombat && kept === 20 && deterministicNatural === null) {
    const fortune = ctx.read.value(creature, 'fortune');
    if (fortune == null) handBack('caster_gains_luck:fortune-not-held');
    const before = requiredInt(ecLean(creature, 'luck'), 'skill_roll_arr:invalid-luck');
    const after = Math.min(before + 1, requiredInt(fortune, 'caster_gains_luck:invalid-fortune'));
    ec(creature).luck = after;
    if (after > before) {
      if (hasLuckTrigger(ctx, creature, 'triggers_when_caster_gains_luck'))
        handBack('caster_gains_luck:trigger-carrier');
      applyUniversalFortunateOnLuckGain(ctx, creature);
    }
  }
  if (inCombat && kept === 1 && ec(creature).encounter_id != null && deterministicNatural === null) {
    if (hasLuckTrigger(ctx, creature, 'triggers_on_unlucky_roll')) handBack('caster_loses_luck:trigger-carrier');
    ec(creature).luck = Math.max(-1, requiredInt(ecLean(creature, 'luck'), 'skill_roll_arr:invalid-luck') - 1);
  }
  insertRoll(ctx, { roll_type_1: options?.rowType ?? options?.rollType ?? CONTEST_ROLL_ID, roll_type_2: skill.id, roll_item_id: options && 'rollItemId' in options ? options.rollItemId : input.abilityId, by: creature, to, roll_result: result,
    rolls_full: options?.coverTier === undefined && !(options?.criticalThreshold != null && options.criticalThreshold < 20) ? fullFaces
      : fullFaces.map(face => Array.isArray(face) && /^d20(?:_|\s|$)/.test(String(face[1]))
        ? [face[0], face[1], { ...record(face[2]), ...(options?.coverTier === undefined ? {} : { cover_tier: options.coverTier }),
          ...(options?.criticalThreshold != null && options.criticalThreshold < 20 ? { critical_hit_threshold: options.criticalThreshold } : {}) }] : face),
    dice: 1, die: 20, mod: manual.modifier + tierBonus, adv_dis_stacks: skill.adv, dc,
    trace: skill.advDisTrace,
    ...(options?.houseRule ? { house_rule: options.houseRule } : {}) });
  if (deterministicNatural === null) ascensionSkillPointGain(ctx, creature, skill, kept, undefined, ascensionThreshold);
  return result;
}

function skillAscensionThreshold(ctx: JanusCtx, creature: Row, skill: Pick<SelectedSkill, 'tier'>): number | null {
  // Read the roller's compiled layer once,
  // including its passive/status sources. It changes the natural-face threshold,
  // not the skill total or the secondary d20's DC (-1 admits natural 19 and 20).
  const canAscend = skill.tier > 0 && String(ecLean(creature, 'item_type')) === 'player_character'
    && !!ecLean(creature, 'encounter_id');
  if (!canAscend) return null;
  const ascensionRangeMod = ctx.read.value(creature, 'ascension_skill_point_range_mod');
  return 20 + (ascensionRangeMod == null ? 0
    : requiredInt(ascensionRangeMod, 'check_ascension_skill_point_gain:invalid-range-mod'));
}

/** Native skill and perfect-minigame ascension share the same natural gate, confirmation, SP and tier rows. */
export function ascensionSkillPointGain(ctx: JanusCtx, creature: Row, skill: Pick<SelectedSkill, 'id' | 'tier'>, natural: number,
  skillVariable?: string, threshold = skillAscensionThreshold(ctx, creature, skill)): void {
  if (threshold !== null && natural >= threshold) {
    if (!record(creature.player_characters)) handBack('check_ascension_skill_point_gain:pc-row-not-held');
    const skillDef = skillVariable ? null : ctx.read.definition(skill.id);
    if (!skillVariable && !skillDef) definitionNotHeld('check_ascension_skill_point_gain:skill-definition-not-held', skill.id);
    const variable = skillVariable ?? String(itemField(skillDef, 'item_name') ?? '').toLowerCase()
      .replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    const entry = record(casterSkills(creature)[variable]);
    if (!entry) handBack('check_ascension_skill_point_gain:skill-entry-not-held');
    if (requiredInt(entry.tier_level, 'check_ascension_skill_point_gain:invalid-tier') > 0) {
      // The pre-cast compiled projection can omit roll.die; skill_roll_arr
      // builds it from tier_level before calling the ascension helper.
      const die = record(entry.roll)?.die == null ? skill.tier + 3
        : requiredInt(record(entry.roll)?.die, 'check_ascension_skill_point_gain:invalid-die');
      if (die > 0) {
        const face = ctx.roll.d(20);
        const pc = record(creature.player_characters)!;
        const totalSp = ['sp_a_', 'sp_t_', 'sp_as_'].reduce((sum, prefix) =>
          sum + requiredInt(pcField(creature, prefix + variable) ?? 0, 'check_ascension_skill_point_gain:invalid-sp'), 0);
        if (face >= die) {
          const column = `sp_as_${variable}`;
          // The saved EC item_id links this instance to its PC even when the PC projection omits that duplicate identity.
          const pcId = upper(pcField(creature, 'item_id') ?? ecLean(creature, 'item_id'));
          if (!pcId) handBack('check_ascension_skill_point_gain:pc-id-not-held');
          const priorAscension = requiredInt(pcField(creature, column) ?? 0, 'check_ascension_skill_point_gain:invalid-pc-sp');
          const nextAscension = priorAscension + 1;
          (ctx.playerCharactersChanged ??= []).push({ item_id: pcId, skill_id: skill.id,
            column, before: priorAscension, after: nextAscension });
          pc[column] = nextAscension;
          insertRoll(ctx, { roll_type_1: 'gained_ascension_skill_point', roll_type_2: '',
            roll_item_id: skill.id, by: creature, roll_result: face,
            rolls_full: [[face, 'd20_1', { kept: true }], [totalSp + 1, 'sp_total_after']],
            dice: 1, die: 20, mod: 0, adv_dis_stacks: 0, dc: die });
          const previousTier = skillTierForSp(totalSp), nextTier = skillTierForSp(totalSp + 1);
          if (nextTier > previousTier)
            insertRoll(ctx, { roll_type_1: 'ascension_skill_tier_up', roll_type_2: `skill_tier_${nextTier}`,
              roll_item_id: skill.id, by: creature, roll_result: nextTier, dc: previousTier });
          return;
        }
        insertRoll(ctx, { roll_type_1: 'failed_roll_for_ascension_skill_point', roll_type_2: '',
          roll_item_id: skill.id, by: creature, roll_result: face,
          rolls_full: [[face, 'd20_1', { kept: true }], [totalSp, 'sp_total_after']],
          dice: 1, die: 20, mod: 0, adv_dis_stacks: 0, dc: die });
      }
    }
  }
}

/** ft_apply_spell_circle_contest_dis()'s provider walk: does the SPELLCASTER (roll_to) carry target_contesting_roll_dis_vs_spell_circle naming one
 *  of this spell's circles — on the creature, its ability buckets, its feats and ONE level of their grants (find_caster_layer_provider_arrs), or
 *  an applied status? An unheld definition hands back `…definition-not-held:<id>` (the lane fetches and resolves again); a matching grant with
 *  an if_* condition (check_ability_conditions) is not modelled → named hand-back. */
export function spellCircleContestDisMatched(ctx: JanusCtx, to: Row, ability: Row): boolean {
  const circleDis = 'target_contesting_roll_dis_vs_spell_circle';
  // check_uuid(): only well-formed circle ids enter the set
  const circles = new Set(ctx.read.entries(ability, 'spell_circle').map((entry) => upper(layerListEntryUuid(entry)))
    .filter((id) => /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id)));
  if (!circles.size) return false;
  const idOf = (entry: unknown): string => upper(typeof entry === 'string' ? entry
    : record(entry)?.ability_id ?? itemField(record(record(entry)?.item_arr), 'item_id') ?? record(entry)?.item_arr ?? itemField(record(entry), 'item_id'));
  const resolve = (entry: unknown): Row => {
    const nested = record(record(entry)?.item_arr);
    const definition = (nested && record(nested.item_layer_types) ? nested : null) ?? (idOf(entry) ? ctx.read.definition(idOf(entry)) : null);
    // `…definition-not-held:<id>` — the lane fetches the definition and resolves again (ability-truth.ts)
    if (!definition) handBack(`skill_roll_arr:spell-circle-provider-definition-not-held:${idOf(entry)}`);
    return definition!;
  };
  const buckets = ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'attack_ability', 'free_ability', 'reaction_ability',
    'opportunity_attack_ability'];
  const names = (row: Row): boolean => ctx.read.entries(row, circleDis).some((entry) => circles.has(upper(layerListEntryUuid(entry))));
  const conditional = (row: Row): boolean => Object.keys(record(row.item_layer_types) ?? {}).some((key) => key.startsWith('if_'));
  const grant = (row: Row): boolean => {
    if (!names(row)) return false;
    if (conditional(row)) handBack('skill_roll_arr:spell-circle-provider-conditions');
    return true;
  };
  return names(to)
    || buckets.some((bucket) => ctx.read.entries(to, bucket).some((entry) => grant(resolve(entry))))
    || ctx.read.entries(to, 'feat').some((feat) => {
      const definition = resolve(feat);
      return grant(definition) || buckets.some((bucket) => ctx.read.entries(definition, bucket).some((entry) => grant(resolve(entry))));
    })
    || activeStatuses(ctx, to).some((status) => {
      const id = upper(status.status_item_id);
      // PHP reads the APPLIED status_arr; a row carrying its layer bucket is authoritative (even empty) — only a thinned / by-reference
      // status_arr is hydrated from the catalog
      const applied = record(status.status_arr);
      const definition = applied && 'item_layer_types' in applied ? applied : ctx.read.definition(id);
      if (!definition) handBack(`skill_roll_arr:spell-circle-status-definition-not-held:${id}`);
      return names(definition!);
    });
}
